import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  Popover,
  List,
  ListItemButton,
  Divider,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import FiberNewRoundedIcon from '@mui/icons-material/FiberNewRounded';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useAuth } from '../context/AuthContext';
import { useSiteSession } from '../context/SiteSessionContext';
import { getCollection, getCustomerLabel, getId, getSiteLabel, getUserLabel, requestBlob, requestJson, stripEmptyValues, userHasSite } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

const emptyForm = {
  siteId: '',
  savSiteId: '',
  advisorId: '',
  advisor: '',
  requestType: 'sav',
  defect: '',
  replacementPart: '',
  supplier: '',
  brand: '',
  model: '',
  size: '',
  color: '',
  serialNumber: '',
  customerId: '',
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  warrantyStatus: 'Garantie fournisseur',
  status: 'new',
  comment: '',
};

const requestTypeOptions = [
  ['sav', 'SAV'],
  ['lenses', 'Lentilles'],
  ['audio', 'Audio'],
];

const legacyRequestTypeLabels = {
  customer_order: 'Commande client à facturer',
};

const statusOptionGroups = [
  {
    label: 'Nouveau',
    options: [
      ['new', 'Nouvelle demande'],
    ],
  },
  {
    label: 'Fournisseur',
    options: [
      ['ordered', 'Commandé'],
      ['supplier_confirmation', 'En attente confirmation fournisseur'],
      ['supplier_order', 'En attente réponse fournisseur'],
      ['supplier_followup', 'Relance fournisseur'],
    ],
  },
  {
    label: 'Magasin',
    options: [
      ['waiting_store_info', 'En attente réponse magasin'],
      ['store_answer', 'Réponse magasin OK'],
      ['returned_store', 'Envoyé au magasin'],
    ],
  },
  {
    label: 'Terminé',
    options: [
      ['closed', 'Clôturé'],
    ],
  },
  {
    label: 'Autre',
    options: [
      ['cancelled', 'Annulé'],
    ],
  },
];

const statusOptions = statusOptionGroups.flatMap((group) => group.options);

const legacyStatusLabels = {};

const statusCategoryLabels = {
  new: 'Nouveau',
  ordered: 'Fournisseur',
  supplier_confirmation: 'Fournisseur',
  supplier_order: 'Fournisseur',
  supplier_followup: 'Fournisseur',
  waiting_store_info: 'Magasin',
  store_answer: 'Magasin',
  new_comment: 'Magasin',
  returned_store: 'Magasin',
  closed: 'Terminé',
  cancelled: 'Autre',
};

const statusCategoryColors = {
  Nouveau: 'primary',
  Fournisseur: 'secondary',
  Magasin: 'info',
  Terminé: 'success',
  Autre: 'default',
};

const logisticsStatusCards = [
  {
    key: 'new',
    title: 'Nouvelles demandes',
    helper: 'Demandes reçues des magasins',
    icon: <FiberNewRoundedIcon />,
    color: 'primary.main',
    bgcolor: 'rgba(59, 130, 246, 0.10)',
  },
  {
    key: 'ordered',
    title: 'Commandés',
    helper: 'Numéro de commande fournisseur reçu',
    icon: <LocalShippingRoundedIcon />,
    color: 'secondary.main',
    bgcolor: 'rgba(168, 85, 247, 0.10)',
  },
  {
    key: 'supplier_confirmation',
    title: 'En attente confirmation fournisseur',
    helper: 'Numéro de commande fournisseur attendu',
    icon: <LocalShippingRoundedIcon />,
    color: 'secondary.main',
    bgcolor: 'rgba(168, 85, 247, 0.10)',
  },
  {
    key: 'supplier_order',
    title: 'En attente réponse fournisseur',
    helper: 'Retour fournisseur attendu',
    icon: <LocalShippingRoundedIcon />,
    color: 'secondary.main',
    bgcolor: 'rgba(168, 85, 247, 0.10)',
  },
  {
    key: 'supplier_followup',
    title: 'Relance fournisseur',
    helper: 'Fournisseur à relancer',
    icon: <CampaignRoundedIcon />,
    color: 'warning.main',
    bgcolor: 'rgba(245, 158, 11, 0.12)',
  },
  {
    key: 'waiting_store_info',
    title: 'En attente réponse magasin',
    helper: 'Informations demandées au magasin',
    icon: <StoreRoundedIcon />,
    color: 'error.main',
    bgcolor: 'rgba(239, 68, 68, 0.10)',
  },
  {
    key: 'store_answer',
    title: 'Réponse magasin OK',
    helper: 'Réponses reçues du magasin',
    icon: <StoreRoundedIcon />,
    color: 'info.main',
    bgcolor: 'rgba(14, 165, 233, 0.10)',
  },
  {
    key: 'returned_store',
    title: 'Envoyé au magasin',
    helper: 'Dossiers retournés au magasin',
    icon: <TaskAltRoundedIcon />,
    color: 'success.main',
    bgcolor: 'rgba(34, 197, 94, 0.10)',
  },
  {
    key: 'closed',
    title: 'Clôturés',
    helper: 'Dossiers terminés',
    icon: <TaskAltRoundedIcon />,
    color: 'success.main',
    bgcolor: 'rgba(34, 197, 94, 0.10)',
  },
];

const logisticsCardSections = [
  { title: 'Nouveau', keys: ['new'] },
  {
    title: 'Fournisseur',
    keys: ['ordered', 'supplier_confirmation', 'supplier_order', 'supplier_followup'],
  },
  { title: 'Magasin', keys: ['waiting_store_info', 'store_answer', 'returned_store'] },
  { title: 'Terminé', keys: ['closed'] },
];

const logisticsAlertCards = [
  {
    key: 'late_store_response',
    title: 'Alerte réponse tardive magasin',
    helper: 'En attente de réponse depuis plus de 5 jours',
    icon: <AccessTimeRoundedIcon />,
    color: 'error.main',
    bgcolor: 'rgba(239, 68, 68, 0.10)',
  },
  {
    key: 'late_supplier_response',
    title: 'Alerte réponse tardive fournisseur',
    helper: 'En attente de réponse depuis plus de 5 jours',
    icon: <AccessTimeRoundedIcon />,
    color: 'warning.main',
    bgcolor: 'rgba(245, 158, 11, 0.12)',
  },
  {
    key: 'supplier_followup',
    title: 'Relances fournisseur',
    helper: 'Dossiers à relancer auprès du fournisseur',
    icon: <CampaignRoundedIcon />,
    color: 'secondary.main',
    bgcolor: 'rgba(168, 85, 247, 0.10)',
  },
];

const storeStatusCards = [
  {
    key: 'new',
    title: 'Demandes envoyées',
    helper: 'En attente de prise en charge',
    icon: <FiberNewRoundedIcon />,
    color: 'primary.main',
    bgcolor: 'rgba(59, 130, 246, 0.10)',
  },
  {
    key: 'waiting_store_info',
    title: 'À répondre',
    helper: 'Information demandée par la Logistique',
    icon: <StoreRoundedIcon />,
    color: 'warning.main',
    bgcolor: 'rgba(245, 158, 11, 0.12)',
  },
  {
    key: 'returned_store',
    title: 'SAV reçus',
    helper: 'Demandes renvoyées par la Logistique',
    icon: <MarkEmailUnreadRoundedIcon />,
    color: 'info.main',
    bgcolor: 'rgba(14, 165, 233, 0.10)',
  },
];

const warrantyOptions = [
  ['supplier_warranty', 'Garantie fournisseur'],
  ['customer_quote', 'Devis client'],
  ['out_of_warranty', 'Hors garantie'],
  ['unknown', 'À confirmer'],
];

const legacyWarrantyLabels = {
  supplier_warranty: 'Garantie fournisseur',
  customer_quote: 'Commande client à facturer',
  out_of_warranty: 'Hors garantie',
  unknown: 'À confirmer',
};

const ATTACHMENT_REQUIREMENT_LABELS = {
  fullFramePhoto: 'photo monture complète',
  defectivePartPhoto: 'photo pièce défectueuse',
  mandatoryForm: 'formulaire obligatoire',
  purchaseInvoice: 'facture d’achat client/fournisseur',
  defectVideoRequired: 'vidéo du défaut',
};

function getAttachmentRequirementLabel(type) {
  if (type === 'standard') return 'Pièce jointe obligatoire';
  if (type === 'other' || !type) return 'Autre fichier';
  return ATTACHMENT_REQUIREMENT_LABELS[type] || 'Autre fichier';
}

const defaultSavSettings = {
  defects: ['RAYURE', 'CASSE', 'DEFORMATION', 'BRULURE', 'TRAITEMENT', 'INVERSION VERRE', 'COMMANDE', 'ECLAT'],
  replacementParts: ['1 MONTURE COMPLETE', '1 BRANCHE DROITE', '1 BRANCHE GAUCHE', '1 FACE', '1 VERRE GAUCHE', '1 VERRE DROIT', '1 PAIRE DE VERRES'],
  suppliers: ['LUXOTTICA', 'SAFILO', 'OPAL', 'MARCOLIN'],
  brands: ['AD LIB', 'ADIDAS', 'ADIDAS JUNIOR', 'AIRLIGHT', 'ALEXANDER MAC QUEEN', 'ALIBI', 'ANA HICKMANN', 'ANDY BROOK', 'ARNETTE', 'ART KENTIEL', 'Attitude Santé', 'AVENGERS', 'AWEAR', 'AZUR', 'BAZZARO', 'B JENIE HILLS', 'B KOSBY SON', 'BA&SH', 'BALI EYEWEAR', 'RAY BAN', 'OAKLEY', 'VOGUE', 'CARRERA', 'VERSACE', 'DOLCE & GABANA', 'GUESS'],
  warranties: ['Garantie fournisseur', 'Garantie interne payante', 'Hors garantie', 'Commande client à facturer'],
  completeFrameAllowedSuppliers: ['LUXOTTICA', 'SAFILO'],
  supplierAttachmentRules: [],
  brandAttachmentRules: [],
  supplierBrandRules: [],
};

const MAX_ATTACHMENT_COUNT = 5;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_ATTACHMENT_SIZE = 50 * 1024 * 1024;

// Les vidéos (défaut META…) ont une limite plus large que les photos et documents.
function getAttachmentSizeError(file) {
  const isVideo = isVideoAttachment(file);
  const maxSize = isVideo ? MAX_VIDEO_ATTACHMENT_SIZE : MAX_ATTACHMENT_SIZE;
  if (!file || file.size <= maxSize) return '';
  return `Le fichier ${file.name} fait ${(file.size / 1024 / 1024).toFixed(1)} Mo : maximum ${isVideo ? '50 Mo pour une vidéo' : '10 Mo'}.`;
}
const DAY_MS = 24 * 60 * 60 * 1000;
const ORDERED_STATUS_ALERT_DAYS = 42;

function normalizeSupplier(value) {
  return String(value || '').trim().toUpperCase();
}

function normalizeForRule(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
}

function isCompleteFramePart(value) {
  const normalized = normalizeForRule(value);
  return normalized.includes('MONTURE COMPLETE') || normalized.includes('MONTURE NUE');
}

function isSupplierAllowedCompleteFrame(value, allowedSuppliers = []) {
  return allowedSuppliers.map(normalizeForRule).filter(Boolean).includes(normalizeForRule(value));
}

function isSupplierWarranty(value) {
  return ['GARANTIE FOURNISSEUR', 'SUPPLIER_WARRANTY'].includes(normalizeForRule(value));
}

function isOrderWithoutSavAttachments(value) {
  const normalized = normalizeForRule(value);
  return normalized.includes('COMMANDE CLIENT') || normalized.includes('GARANTIE INTERNE') || normalized.includes('HORS GARANTIE') || ['CUSTOMER_QUOTE', 'OUT_OF_WARRANTY'].includes(normalized);
}

function isBlockedCompleteFrameSav(form, allowedSuppliers) {
  return (
    (form.requestType || 'sav') === 'sav' &&
    isSupplierWarranty(form.warrantyStatus) &&
    isCompleteFramePart(form.replacementPart) &&
    form.supplier &&
    !isSupplierAllowedCompleteFrame(form.supplier, allowedSuppliers)
  );
}

function getRequiredAttachmentEntries(...rules) {
  return Object.entries(ATTACHMENT_REQUIREMENT_LABELS)
    .filter(([type]) => rules.some((rule) => rule?.[type]))
    .map(([type, label]) => ({ type, label }));
}

function isVideoAttachment(file) {
  const mimeType = String(file?.type || file?.mimeType || '').toLowerCase();
  const fileName = String(file?.name || file?.originalName || file?.fileName || '').toLowerCase();
  return mimeType.startsWith('video/') || /\.(mp4|mov|m4v|avi|webm|mkv|3gp)$/i.test(fileName);
}

function getOptionLabel(options, value) {
  return options.find(([key]) => key === value)?.[1]
    || legacyRequestTypeLabels[value]
    || legacyStatusLabels[value]
    || value
    || '—';
}

function renderStatusOptionGroups() {
  return statusOptionGroups.flatMap((group) => [
    <ListSubheader key={`${group.label}-header`} disableSticky>
      {group.label}
    </ListSubheader>,
    ...group.options.map(([value, label]) => (
      <MenuItem key={value} value={value}>
        {label}
      </MenuItem>
    )),
  ]);
}

function matchesSelection(value, selected = []) {
  return !selected.length || selected.includes(value || '');
}

const EMPTY_COLUMN_FILTERS = {
  site: [],
  customer: [],
  requestType: [],
  supplier: [],
  brand: [],
  model: [],
  replacementPart: [],
  status: [],
};

const FILTER_LIST_LIMIT = 200;

const SAV_SORT_VALUE_GETTERS = {
  createdAt: (row) => new Date(row.createdAt || 0).getTime(),
  updatedAt: (row) => new Date(row.updatedAt || 0).getTime(),
  supplier: (row) => `${row.supplier || ''} ${row.brand || ''}`.trim(),
  customer: (row) => row.customerName || '',
  status: (row) => row.statusLabel || '',
  warranty: (row) => row.warrantyLabel || '',
  site: (row) => `${row.siteLabel || ''} ${row.advisor || ''}`.trim(),
  advisor: (row) => row.advisor || '',
};

function compareSavRows(left, right, { field, direction }) {
  const getValue = SAV_SORT_VALUE_GETTERS[field] || SAV_SORT_VALUE_GETTERS.createdAt;
  const leftValue = getValue(left);
  const rightValue = getValue(right);
  let comparison = typeof leftValue === 'number'
    ? leftValue - rightValue
    : String(leftValue).localeCompare(String(rightValue), 'fr', { numeric: true, sensitivity: 'base' });
  // Les valeurs vides restent en fin de liste quel que soit le sens.
  if (!leftValue && rightValue) return 1;
  if (leftValue && !rightValue) return -1;
  if (!comparison) comparison = SAV_SORT_VALUE_GETTERS.createdAt(right) - SAV_SORT_VALUE_GETTERS.createdAt(left);
  else if (direction === 'desc') comparison = -comparison;
  return comparison;
}

// Bouton de filtre multi-valeurs : liste à cocher avec recherche et compteurs.
function FilterButton({ label, icon, options, selected, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const normalizedSearch = search.trim().toLowerCase();
  const visibleOptions = options.filter(([value]) => !normalizedSearch || String(value).toLowerCase().includes(normalizedSearch));
  const active = selected.length > 0;
  const toggle = (value) => onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);

  return (
    <>
      <Button
        size="small"
        variant={active ? 'contained' : 'outlined'}
        color={active ? 'primary' : 'inherit'}
        startIcon={icon}
        endIcon={active ? (
          <CloseRoundedIcon
            fontSize="small"
            aria-label={`Effacer le filtre ${label}`}
            onClick={(event) => { event.stopPropagation(); onChange([]); }}
          />
        ) : null}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ fontWeight: 850, textTransform: 'none', borderColor: active ? undefined : 'divider', bgcolor: active ? undefined : 'background.paper' }}
      >
        {active ? `${label} (${selected.length})` : label}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setSearch(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 300, mt: 0.5 } } }}
      >
        <Box sx={{ p: 1.25 }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder={`Rechercher ${label.toLowerCase()}`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Box>
        <Divider />
        <List dense sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
          {visibleOptions.slice(0, FILTER_LIST_LIMIT).map(([value, count]) => (
            <ListItemButton key={value} onClick={() => toggle(value)} sx={{ py: 0.25 }}>
              <Checkbox size="small" edge="start" checked={selected.includes(value)} tabIndex={-1} disableRipple />
              <ListItemText primary={value} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{count}</Typography>
            </ListItemButton>
          ))}
          {!visibleOptions.length && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>Aucune valeur.</Typography>
          )}
          {visibleOptions.length > FILTER_LIST_LIMIT && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
              {visibleOptions.length - FILTER_LIST_LIMIT} autres valeurs : affine la recherche.
            </Typography>
          )}
        </List>
        <Divider />
        <Stack direction="row" spacing={1} sx={{ p: 1.25 }}>
          <Button fullWidth size="small" variant="outlined" disabled={!active} onClick={() => onChange([])}>Tout effacer</Button>
          <Button fullWidth size="small" variant="contained" onClick={() => { setAnchorEl(null); setSearch(''); }}>Fermer</Button>
        </Stack>
      </Popover>
    </>
  );
}

function getStatusColor(status) {
  if (status === 'cancelled') return 'default';
  if (status === 'closed') return 'success';
  if (status === 'waiting_store_info') return 'warning';
  if (status === 'returned_store') return 'primary';
  if (status === 'supplier_followup' || status === 'supplier_order' || status === 'ordered') return 'secondary';
  if (status === 'supplier_confirmation' || status === 'store_answer' || status === 'new_comment') return 'info';
  return 'primary';
}

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#db2777', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0d9488', '#0891b2', '#4f46e5'];

function getNameInitials(name) {
  const words = String(name || '').split(/\s+/).filter((word) => /^\p{L}/u.test(word));
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('') || '?';
}

// Couleur stable par personne : le même nom garde toujours la même couleur.
function getNameColor(name) {
  const value = String(name || '').trim().toUpperCase();
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// Rond pour le conseiller, carré arrondi pour le client.
function PersonCell({ name, secondary, variant = 'circular' }) {
  if (!name) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Avatar
        variant={variant}
        sx={{ width: 28, height: 28, flex: '0 0 28px', fontSize: '0.7rem', fontWeight: 900, bgcolor: getNameColor(name), borderRadius: variant === 'rounded' ? 1.5 : '50%' }}
      >
        {getNameInitials(name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {name}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {secondary}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

const ROW_ICON_BUTTON_SX = { color: 'common.white', width: 30, height: 30 };

function getFollowupActionLabel(row, storeInfoAnswer) {
  if (!storeInfoAnswer) return 'Suivi / mise à jour du statut';
  return row?.status === 'waiting_store_info' ? 'Répondre à la Logistique' : 'Nouveau commentaire';
}

function getShortWarrantyLabel(label) {
  return String(label || '')
    .replace(/^Commande\b/i, 'Cde')
    .replace(/\bfournisseur\b/i, 'fourn.')
    .replace(/\sà facturer$/i, ' à fact.');
}

function formatRelativeSince(value) {
  if (!value) return '';
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60000));
  if (Number.isNaN(minutes)) return '';
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 24 * 60) return `il y a ${Math.floor(minutes / 60)} h`;
  return `il y a ${Math.floor(minutes / (24 * 60))} j`;
}

function getWarrantyColor(value) {
  const label = String(value || '').toLowerCase();
  if (label.includes('facturer') || label.includes('devis')) return 'warning';
  if (label.includes('hors')) return 'error';
  if (label.includes('interne')) return 'secondary';
  if (label.includes('garantie')) return 'success';
  return 'default';
}

function getSavReference(row) {
  return row?.requestNumber || 'SAV non attribué';
}

function getSiteCodeLabel(site) {
  if (!site) return '';
  if (typeof site === 'string') return site;
  return site.code || getSiteLabel(site);
}

function getShortSavReference(row) {
  return getSavReference(row);
}

const CODE_39_PATTERNS = {
  '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', A: 'wnnnnwnnw', B: 'nnwnnwnnw',
  C: 'wnwnnwnnn', D: 'nnnnwwnnw', E: 'wnnnwwnnn', F: 'nnwnwwnnn',
  G: 'nnnnnwwnw', H: 'wnnnnwwnn', I: 'nnwnnwwnn', J: 'nnnnwwwnn',
  K: 'wnnnnnnww', L: 'nnwnnnnww', M: 'wnwnnnnwn', N: 'nnnnwnnww',
  O: 'wnnnwnnwn', P: 'nnwnwnnwn', Q: 'nnnnnnwww', R: 'wnnnnnwwn',
  S: 'nnwnnnwwn', T: 'nnnnwnwwn', U: 'wwnnnnnnw', V: 'nwwnnnnnw',
  W: 'wwwnnnnnn', X: 'nwnnwnnnw', Y: 'wwnnwnnnn', Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw', '*': 'nwnnwnwnn',
};

function getSavBarcodeSvg(value) {
  const normalized = String(value || '').toUpperCase().replace(/[^0-9A-Z-]/g, '');
  const payload = `*${normalized}*`;
  const narrow = 2;
  const wide = 5;
  const quietZone = 12;
  let x = quietZone;
  const bars = [];

  [...payload].forEach((character) => {
    const pattern = CODE_39_PATTERNS[character];
    if (!pattern) return;
    [...pattern].forEach((widthType, index) => {
      const width = widthType === 'w' ? wide : narrow;
      if (index % 2 === 0) bars.push(`<rect x="${x}" y="4" width="${width}" height="44" fill="#111827"/>`);
      x += width;
    });
    x += narrow;
  });
  const width = x + quietZone;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} 64" role="img" aria-label="Code barre ${normalized}" preserveAspectRatio="none"><rect width="100%" height="100%" fill="#ffffff"/>${bars.join('')}<text x="${width / 2}" y="60" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="700" fill="#111827">${normalized}</text></svg>`;
}

function escapePrintHtml(value) {
  return String(value || '—')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatFileSize(size) {
  const value = Number(size || 0);
  if (!value) return '—';
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} Ko`;
  return `${(value / 1024 / 1024).toFixed(2)} Mo`;
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('fr-FR') : '—';
}

function getStatusHistory(row) {
  return [...(row?.statusHistory || [])].sort((a, b) => new Date(a.changedAt || 0) - new Date(b.changedAt || 0));
}

function getCurrentStatusSince(row) {
  const history = getStatusHistory(row);
  const currentStatus = row?.status || 'new';
  const entry = [...history].reverse().find((item) => item.toStatus === currentStatus);
  return entry?.changedAt || row?.updatedAt || row?.createdAt || '';
}

function getDaysSince(value) {
  if (!value) return 0;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / DAY_MS));
}

function getDisplayLogisticsStatus(row) {
  return row?.status || 'new';
}

function getStatusActorLabel(entry) {
  const userLabel = entry?.changedByUserName || [entry?.changedByUserId?.firstName, entry?.changedByUserId?.lastName].filter(Boolean).join(' ');
  const siteLabel = getSiteLabel(entry?.changedBySiteId);
  if (userLabel && siteLabel && siteLabel !== '—') return `${userLabel} · ${siteLabel}`;
  if (userLabel) return userLabel;
  if (siteLabel && siteLabel !== '—') return siteLabel;
  return entry?.changedBySiteType || 'unknown';
}

function getActivityActorLabel(entry) {
  const userLabel = entry?.performedByUserName || [entry?.performedByUserId?.firstName, entry?.performedByUserId?.lastName].filter(Boolean).join(' ');
  const siteLabel = getSiteLabel(entry?.performedBySiteId);
  if (userLabel && siteLabel && siteLabel !== '—') return `${userLabel} · ${siteLabel}`;
  if (userLabel) return userLabel;
  if (siteLabel && siteLabel !== '—') return siteLabel;
  return entry?.performedBySiteType || 'unknown';
}

function SavActivityHistory({ row }) {
  const activities = [...(row?.activityHistory || [])].sort(
    (left, right) => new Date(right.occurredAt || 0) - new Date(left.occurredAt || 0)
  );

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.light',
              color: 'primary.main',
            }}
          >
            <UpdateRoundedIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
            Journal d'activité
          </Typography>
        </Stack>
        {activities.length ? (
          <Stack spacing={1}>
            {activities.map((activity, index) => (
              <Box
                key={activity._id || `${activity.action}-${activity.occurredAt || index}`}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1.1 }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {activity.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(activity.occurredAt)} · {getActivityActorLabel(activity)}
                  </Typography>
                </Stack>
                {activity.detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
                    {activity.detail}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        ) : (
          <Alert severity="info">Aucune action détaillée n’a encore été enregistrée sur ce dossier.</Alert>
        )}
      </CardContent>
    </Card>
  );
}

function SavStatusWorkflowVertical({ row, children }) {
  const history = getStatusHistory(row);
  const currentStatus = row?.status || 'new';
  const statusTimeline = history.length
    ? history
    : [
        {
          toStatus: currentStatus,
          comment: row?.comment || 'Création du dossier Logistique',
          changedAt: row?.createdAt,
          changedBySiteType: row?.createdBySiteType || 'store',
          changedBySiteId: row?.siteId,
        },
      ];
  const modificationTimeline = (row?.activityHistory || [])
    .filter((activity) => activity.action === 'updated')
    .map((activity) => ({
      ...activity,
      entryType: 'modification',
      changedAt: activity.occurredAt,
    }));
  const timeline = [...statusTimeline, ...modificationTimeline].sort(
    (left, right) => new Date(left.changedAt || 0) - new Date(right.changedAt || 0)
  );
  const activeStep = Math.max(0, timeline.length - 1);

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.light',
                color: 'primary.main',
              }}
            >
              <HistoryRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
              Historique
            </Typography>
          </Stack>
          <Chip size="small" label={getOptionLabel(statusOptions, currentStatus)} color={getStatusColor(currentStatus)} sx={{ fontWeight: 850 }} />
        </Stack>
        <Stepper activeStep={activeStep} orientation="vertical">
          {timeline.map((entry, index) => {
            const isModification = entry.entryType === 'modification';
            const status = entry.toStatus || currentStatus;
            const category = statusCategoryLabels[status] || 'Fournisseur';
            const completed = index < activeStep;
            return (
              <Step key={entry._id || `${status}-${entry.changedAt || index}`} completed={completed}>
                <StepLabel
                  optional={
                    <Stack spacing={0.25}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(entry.changedAt)} · {isModification ? getActivityActorLabel(entry) : getStatusActorLabel(entry)}
                      </Typography>
                      {(isModification ? entry.detail : entry.comment) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {isModification ? entry.detail : entry.comment}
                        </Typography>
                      )}
                    </Stack>
                  }
                >
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {isModification ? 'Dossier modifié' : getOptionLabel(statusOptions, status)}
                    </Typography>
                    <Chip
                      size="small"
                      label={isModification ? 'Modification' : category}
                      color={isModification ? 'warning' : statusCategoryColors[category] || 'secondary'}
                      variant="outlined"
                      sx={{ height: 19, fontSize: 10, fontWeight: 850 }}
                    />
                  </Stack>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {currentStatus === 'cancelled' && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Dossier annulé.
          </Alert>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function StatusReplyForm({
  detailTarget,
  statusForm,
  setStatusForm,
  statusError,
  statusSaving,
  statusAttachmentFiles,
  canStoreAnswerInfo,
  canStoreClose,
  handleStatusSubmit,
  handleStatusAttachmentChange,
  removeSelectedStatusAttachment,
}) {
  return (
    <Box component="form" onSubmit={handleStatusSubmit} sx={{ mt: 2.25, pl: { sm: 4.5 } }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'action.hover',
              color: 'primary.main',
            }}
          >
            <UpdateRoundedIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>
            Mettre à jour
          </Typography>
        </Stack>
        {statusError && <Alert severity="error">{statusError}</Alert>}
        <TextField
          label="Statut"
          select
          value={statusForm.status}
          onChange={(event) => setStatusForm((current) => ({ ...current, status: event.target.value }))}
          disabled={Boolean(canStoreAnswerInfo(detailTarget) || canStoreClose(detailTarget))}
          required
          fullWidth
          size="small"
        >
          {statusForm.status === 'new_comment' && <MenuItem value="new_comment">Statut inchangé</MenuItem>}
          {renderStatusOptionGroups()}
        </TextField>
        {statusForm.status === 'returned_store' && (
          <TextField
            label="Numéro de BL Cosium"
            value={statusForm.cosiumDeliveryNoteNumber || ''}
            onChange={(event) => setStatusForm((current) => ({ ...current, cosiumDeliveryNoteNumber: event.target.value }))}
            required
            fullWidth
            size="small"
            helperText="Obligatoire pour envoyer le dossier au magasin."
          />
        )}
        {statusForm.status === 'ordered' && (
          <TextField
            label="Numéro de commande fournisseur"
            value={statusForm.supplierOrderNumber || ''}
            onChange={(event) => setStatusForm((current) => ({ ...current, supplierOrderNumber: event.target.value }))}
            required
            fullWidth
            size="small"
            helperText="Obligatoire pour passer le dossier à Commandé."
          />
        )}
        <TextField
          label="Écrire un commentaire"
          value={statusForm.comment}
          onChange={(event) => setStatusForm((current) => ({ ...current, comment: event.target.value }))}
          required={statusForm.status !== 'ordered'}
          multiline
          minRows={2}
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} sx={{ alignSelf: { sm: 'flex-start' } }}>
            Ajouter des fichiers
            <input
              type="file"
              hidden
              multiple
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={handleStatusAttachmentChange}
            />
          </Button>
          <Button type="submit" variant="contained" disabled={statusSaving}>
            {statusSaving ? 'Mise à jour…' : canStoreAnswerInfo(detailTarget) ? (statusForm.status === 'store_answer' ? 'Envoyer la réponse' : 'Ajouter le commentaire') : canStoreClose(detailTarget) ? 'Clôturer' : 'Valider'}
          </Button>
        </Stack>
        {statusAttachmentFiles.length > 0 && (
          <Stack spacing={0.75}>
            {statusAttachmentFiles.map((file, index) => (
              <Stack
                key={`${file.name}-${index}`}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.75 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                  {file.name} · {formatFileSize(file.size)}
                </Typography>
                <IconButton size="small" onClick={() => removeSelectedStatusAttachment(index)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

function DetailField({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function FormSection({ title, icon, children }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(102,178,255,0.12)',
              color: 'primary.main',
              '& svg': { fontSize: 19 },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
          {title}
        </Typography>
      </Stack>
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}

function DetailCardSection({ title, icon, children, sx }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', ...sx }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(102,178,255,0.12)',
              color: 'primary.main',
              '& svg': { fontSize: 19 },
            }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
            {title}
          </Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

const logisticsGridSx = {
  border: 0,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: 'rgba(102,178,255,0.14)',
    fontWeight: 900,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 900,
    lineHeight: 1.15,
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    py: 0,
    lineHeight: 1.25,
  },
  '& .MuiDataGrid-cellContent': {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiChip-root': {
    alignSelf: 'center',
  },
};

// Keep credential keystrokes local to the dialog.  The logistics page can
// contain hundreds of rows; updating SavPage state for every character would
// otherwise reconcile that entire tree while the collaborator is typing.
const LogisticsPortalDialog = memo(function LogisticsPortalDialog({ open, onSubmit, onLogout, loading, error }) {
  const [credentials, setCredentials] = useState({ login: '', password: '' });

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    onSubmit(credentials);
  }, [credentials, onSubmit]);

  return (
    <Dialog
      open={open}
      fullScreen
      disableEscapeKeyDown
      PaperProps={{ sx: { bgcolor: 'background.default', backgroundImage: 'radial-gradient(circle at center, rgba(21, 94, 150, 0.18) 0%, rgba(3, 7, 13, 0) 48%)' } }}
    >
      <Box sx={{ minHeight: '100%', display: 'grid', placeItems: 'center', p: 3 }}>
        <Card variant="outlined" sx={{ width: '100%', maxWidth: 560, borderColor: 'rgba(102,178,255,0.18)', boxShadow: '0 20px 50px rgba(0,0,0,0.18)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Stack alignItems="center" spacing={0.75} sx={{ pb: 1 }}>
                  <Box component="img" src="/optibox-logo.svg" alt="Optibox" sx={{ width: 120, height: 120, objectFit: 'contain' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase' }}>Optibox</Typography>
                </Stack>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 750 }}>Portail Logistique</Typography>
                  <Typography variant="body2" color="text.secondary">Identifie-toi pour accéder aux dossiers Logistique.</Typography>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                  label="Identifiant"
                  placeholder="Matricule ou email"
                  value={credentials.login}
                  onChange={(event) => setCredentials((current) => ({ ...current, login: event.target.value }))}
                  autoFocus
                  required
                  fullWidth
                />
                <TextField
                  label="Mot de passe"
                  type="password"
                  value={credentials.password}
                  onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
                  autoComplete="current-password"
                  required
                  fullWidth
                />
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button type="button" onClick={onLogout} disabled={loading}>Quitter</Button>
                  <Button type="submit" variant="contained" size="large" disabled={loading}>
                    {loading ? 'Connexion…' : 'Se connecter'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Dialog>
  );
});

export default function SavPage({ documentsOnly = false }) {
  const [searchParams] = useSearchParams();
  const openedSearchResultRef = useRef('');
  const requestedFetchRef = useRef('');
  const menuPage = useMenuPage(documentsOnly ? '/sav/documents' : '/sav');
  const { isAdmin, site, siteType, setSession } = useSiteSession();
  const { account, login, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [savSettings, setSavSettings] = useState(defaultSavSettings);
  const [supplierAttachmentRules, setSupplierAttachmentRules] = useState([]);
  const [brandAttachmentRules, setBrandAttachmentRules] = useState([]);
  const [blockedThresholdDays, setBlockedThresholdDays] = useState(3);
  const [form, setForm] = useState(emptyForm);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [supplierOrderTarget, setSupplierOrderTarget] = useState(null);
  const [supplierOrderForm, setSupplierOrderForm] = useState({ supplierOrderNumber: '', comment: '' });
  const [supplierOrderSaving, setSupplierOrderSaving] = useState(false);
  const [supplierOrderError, setSupplierOrderError] = useState('');
  const [detailTarget, setDetailTarget] = useState(null);
  const [detailMode, setDetailMode] = useState('details');
  const [statusForm, setStatusForm] = useState({ status: 'new', comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
  const [statusAttachmentFiles, setStatusAttachmentFiles] = useState([]);
  const [statusSaving, setStatusSaving] = useState(false);
  const [preview, setPreview] = useState({ open: false, url: '', mimeType: '', title: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [documentTarget, setDocumentTarget] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedSavIds, setSelectedSavIds] = useState([]);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusForm, setBulkStatusForm] = useState({ status: 'new', comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
  const [bulkStatusSaving, setBulkStatusSaving] = useState(false);
  const [bulkStatusError, setBulkStatusError] = useState('');
  const [open, setOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  const [savSort, setSavSort] = useState({ field: 'createdAt', direction: 'desc' });
  const [savListMeta, setSavListMeta] = useState({ closedCount: 0, cancelledCount: 0 });
  const openSavCount = useMemo(
    () => rows.filter((row) => !['closed', 'cancelled'].includes(row.status)).length,
    [rows]
  );
  const [savTablePage, setSavTablePage] = useState(0);
  const [savTablePageSize, setSavTablePageSize] = useState(50);
  const [alertFilter, setAlertFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState(EMPTY_COLUMN_FILTERS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [portalError, setPortalError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  const isLogisticsContext = isAdmin || siteType === 'sav';
  const needsLogisticsCollaborator = !isAdmin && siteType === 'sav' && account?.authType === 'site';
  // A site account must authenticate as a logistics collaborator before we
  // load the (large) SAV workspace.  Keeping this gate here prevents the
  // login dialog from competing with the initial 500-row/people loads.
  const canLoadSavData = isAdmin || siteType !== 'sav' || account?.authType === 'logistics_user';
  const canChooseStore = isLogisticsContext;
  const canDelete = isAdmin;
  const canEditRequest = useCallback(
    (row) => isAdmin || (
      siteType === 'sav'
    ) || (
      siteType === 'store' &&
      !['closed', 'cancelled'].includes(row?.status) &&
      (!site?._id || getId(row?.siteId) === site._id)
    ),
    [isAdmin, site?._id, siteType]
  );
  const canStoreAnswerInfo = useCallback(
    (row) => siteType === 'store' && !['closed', 'cancelled'].includes(row?.status) && (!site?._id || getId(row?.siteId) === site._id),
    [site?._id, siteType]
  );
  const canStoreClose = useCallback(
    (row) => siteType === 'store' && row?.status === 'returned_store' && (!site?._id || getId(row?.siteId) === site._id),
    [site?._id, siteType]
  );
  const canUpdateStatus = useCallback(
    (row) => isLogisticsContext || canStoreAnswerInfo(row) || canStoreClose(row),
    [canStoreAnswerInfo, canStoreClose, isLogisticsContext]
  );
  const getDefaultStatusUpdateValue = useCallback(
    (row) => {
      if (canStoreAnswerInfo(row)) return row?.status === 'waiting_store_info' ? 'store_answer' : 'new_comment';
      if (canStoreClose(row)) return 'closed';
      return getDisplayLogisticsStatus(row, blockedThresholdDays);
    },
    [blockedThresholdDays, canStoreAnswerInfo, canStoreClose]
  );
  const storeSites = useMemo(() => sites.filter((item) => item.type === 'store'), [sites]);
  const savSites = useMemo(() => sites.filter((item) => item.type === 'sav'), [sites]);
  const defaultSavSiteId = savSites.length === 1 ? savSites[0]._id : '';
  const selectedStoreSiteId = form.siteId;
  const advisorOptions = useMemo(
    () =>
      users.filter(
        (user) =>
          user.active !== false &&
          (!selectedStoreSiteId || userHasSite(user, selectedStoreSiteId))
      ),
    [selectedStoreSiteId, users]
  );
  const customerOptions = useMemo(
    () => customers.filter((customer) => customer.active !== false),
    [customers]
  );
  const availableBrandOptions = useMemo(() => {
    const supplier = normalizeSupplier(form.supplier);
    const rule = (savSettings.supplierBrandRules || []).find(
      (item) => normalizeSupplier(item?.supplier) === supplier
    );
    if (!supplier || !rule?.brands?.length) return savSettings.brands;
    const allowedBrands = new Set(rule.brands.map((brand) => normalizeSupplier(brand)));
    return savSettings.brands.filter((brand) => allowedBrands.has(normalizeSupplier(brand)));
  }, [form.supplier, savSettings.brands, savSettings.supplierBrandRules]);
  const selectedCustomer = customers.find((customer) => customer._id === form.customerId) || null;
  const selectedAdvisor = users.find((user) => user._id === form.advisorId) || null;
  const editingRow = rows.find((row) => row._id === editingId) || null;
  const selectedSupplierRule = supplierAttachmentRules.find(
    (rule) => normalizeSupplier(rule.supplier) === normalizeSupplier(form.supplier)
  );
  const selectedBrandRule = brandAttachmentRules.find(
    (rule) => normalizeSupplier(rule.brand) === normalizeSupplier(form.brand)
  );
  const attachmentRequirementsExempt = isOrderWithoutSavAttachments(form.warrantyStatus);
  const configuredAttachmentEntries = attachmentRequirementsExempt
    ? []
    : getRequiredAttachmentEntries(selectedSupplierRule, selectedBrandRule);
  const requiredAttachmentEntries = attachmentRequirementsExempt
    ? []
    : configuredAttachmentEntries.length
      ? configuredAttachmentEntries
      : [{ type: 'standard', label: 'pièce jointe obligatoire' }];
  const requiredAttachmentLabels = configuredAttachmentEntries.map((entry) => entry.label);
  const serialNumberRequired = !attachmentRequirementsExempt && [selectedSupplierRule, selectedBrandRule].some((rule) => rule?.serialNumberRequired);
  const defectVideoRequired = !attachmentRequirementsExempt && [selectedSupplierRule, selectedBrandRule].some((rule) => rule?.defectVideoRequired);
  const requiredAttachmentSources = [
    selectedSupplierRule ? 'fournisseur ' + form.supplier : '',
    selectedBrandRule ? 'marque ' + form.brand : '',
  ].filter(Boolean).join(' / ');
  const requiredAttachmentCount = requiredAttachmentEntries.length;
  const currentAttachmentCount = (editingRow?.attachments?.length || 0) + attachmentFiles.length;
  const hasRequiredDefectVideo = !defectVideoRequired || [...(editingRow?.attachments || []), ...attachmentFiles.map((entry) => entry.file)].some(isVideoAttachment);
  const hasTypedAttachments = !editingId
    || attachmentFiles.length > 0
    || (editingRow?.attachments || []).some((attachment) => attachment.requirementType && attachment.requirementType !== 'other');
  const assignedRequirementTypes = new Set([
    ...(editingRow?.attachments || []).map((attachment) => attachment.requirementType),
    ...attachmentFiles.map((entry) => entry.requirementType),
  ].filter(Boolean));
  const missingRequiredAttachmentEntries = hasTypedAttachments
    ? requiredAttachmentEntries.filter((entry) => !assignedRequirementTypes.has(entry.type))
    : [];
  const blockedCompleteFrameSav = isBlockedCompleteFrameSav(form, savSettings.completeFrameAllowedSuppliers);
  const blockedCompleteFrameMessage = 'Monture complète en SAV : facturation fournisseur probable. Sélectionnez uniquement la pièce défectueuse.';

  useEffect(() => {
    if (!defaultSavSiteId || form.savSiteId) return;
    setForm((current) => ({ ...current, savSiteId: current.savSiteId || defaultSavSiteId }));
  }, [defaultSavSiteId, form.savSiteId]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      // Les dossiers clôturés/annulés ne sont chargés que s'ils sont affichés
      // (interrupteur) ou pour la page Documents.
      const includeClosed = showClosed || documentsOnly;
      const payload = await requestJson(`/sav-requests${includeClosed ? '?includeClosed=1' : ''}`);
      setRows(getCollection(payload));
      setSavListMeta({
        closedCount: Number(payload?.meta?.closedCount || 0),
        cancelledCount: Number(payload?.meta?.cancelledCount || 0),
      });
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [documentsOnly, showClosed]);

  const loadSites = useCallback(async () => {
    try {
      const payload = await requestJson('/sites');
      setSites(getCollection(payload).filter((item) => item.active !== false));
    } catch (_err) {
      setSites([]);
    }
  }, []);

  const loadSavSettings = useCallback(async () => {
    try {
      const [payload, suppliersPayload] = await Promise.all([
        requestJson('/settings/SAV'),
        requestJson('/suppliers'),
      ]);
      const supplierRecords = getCollection(suppliersPayload)
        .filter((supplier) => supplier.active !== false);
      const supplierOptions = supplierRecords
        .map((supplier) => supplier.name)
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right, 'fr', { sensitivity: 'base' }));
      const currentSupplierAttachmentRules = supplierRecords
        .filter((supplier) => supplier.name && supplier.attachmentRequirements)
        .map((supplier) => ({
          supplier: supplier.name,
          fullFramePhoto: Boolean(supplier.attachmentRequirements.fullFramePhoto),
          defectivePartPhoto: Boolean(supplier.attachmentRequirements.defectivePartPhoto),
          serialNumberRequired: Boolean(supplier.attachmentRequirements.serialNumberRequired),
          mandatoryForm: Boolean(supplier.attachmentRequirements.mandatoryForm),
          purchaseInvoice: Boolean(supplier.attachmentRequirements.purchaseInvoice),
          defectVideoRequired: Boolean(supplier.attachmentRequirements.defectVideoRequired),
        }));
      setSavSettings({
        defects: payload.value?.defects || defaultSavSettings.defects,
        replacementParts: payload.value?.replacementParts || defaultSavSettings.replacementParts,
        suppliers: supplierOptions.length ? supplierOptions : payload.value?.suppliers || defaultSavSettings.suppliers,
        brands: payload.value?.brands || defaultSavSettings.brands,
        warranties: payload.value?.warranties || defaultSavSettings.warranties,
        completeFrameAllowedSuppliers: payload.value?.completeFrameAllowedSuppliers || defaultSavSettings.completeFrameAllowedSuppliers,
        supplierAttachmentRules: currentSupplierAttachmentRules,
        brandAttachmentRules: payload.value?.brandAttachmentRules || [],
        supplierBrandRules: payload.value?.supplierBrandRules || [],
      });
      setSupplierAttachmentRules(currentSupplierAttachmentRules);
      setBrandAttachmentRules(payload.value?.brandAttachmentRules || []);
    } catch (_err) {
      setSavSettings(defaultSavSettings);
      setSupplierAttachmentRules([]);
      setBrandAttachmentRules([]);
    }
  }, []);

  const loadStockSettings = useCallback(async () => {
    try {
      const payload = await requestJson('/settings/STOCK');
      setBlockedThresholdDays(Number(payload.value?.logisticsBlockedDays || 3));
    } catch (_err) {
      setBlockedThresholdDays(3);
    }
  }, []);

  const loadPeople = useCallback(async () => {
    try {
      const [usersPayload, customersPayload] = await Promise.all([
        requestJson('/users?limit=2000'),
        requestJson('/customers?limit=2000'),
      ]);
      setUsers(getCollection(usersPayload));
      setCustomers(getCollection(customersPayload));
    } catch (_err) {
      setUsers([]);
      setCustomers([]);
    }
  }, []);

  useEffect(() => {
    if (!canLoadSavData) {
      setLoading(false);
      setRows([]);
      setSites([]);
      setUsers([]);
      setCustomers([]);
      return undefined;
    }
    loadSites();
    loadSavSettings();
    loadStockSettings();
    return undefined;
  }, [canLoadSavData, loadSavSettings, loadSites, loadStockSettings]);

  useEffect(() => {
    if (canLoadSavData) loadRows();
  }, [canLoadSavData, loadRows]);

  // People are only needed by the create/edit form.  Defer these two large
  // lists until the form is actually opened instead of blocking the initial
  // logistics view after authentication.
  useEffect(() => {
    if (!canLoadSavData || !open) return undefined;
    loadPeople();
    return undefined;
  }, [canLoadSavData, loadPeople, open]);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q) setQuickFilter(q);
  }, [searchParams]);

  const gridRows = useMemo(
    () =>
      rows.map((row) => {
        const displayStatus = getDisplayLogisticsStatus(row, blockedThresholdDays);
        return {
          ...row,
          id: row._id,
          displayStatus,
          siteLabel: getSiteCodeLabel(row.siteId),
          savSiteLabel: getSiteCodeLabel(row.savSiteId),
          reference: getSavReference(row),
          shortReference: getShortSavReference(row),
          statusLabel: getOptionLabel(statusOptions, displayStatus),
          requestTypeLabel: getOptionLabel(requestTypeOptions, row.requestType || 'sav'),
          warrantyLabel: legacyWarrantyLabels[row.warrantyStatus] || getOptionLabel(warrantyOptions, row.warrantyStatus),
          attachmentCount: row.attachments?.length || 0,
          createdDate: row.createdAt ? new Date(row.createdAt).toLocaleDateString('fr-FR') : '',
          updatedDate: row.updatedAt ? new Date(row.updatedAt).toLocaleString('fr-FR') : '',
          statusSinceAt: getCurrentStatusSince(row),
          blockedDays: getDaysSince(getCurrentStatusSince(row)),
        };
      }),
    [rows, blockedThresholdDays]
  );

  const filterOptions = useMemo(() => {
    const scopedRows = gridRows.filter((row) => documentsOnly || showClosed || !['closed', 'cancelled'].includes(row.status));
    const collect = (selector) => {
      const counts = new Map();
      scopedRows.forEach((row) => {
        const value = selector(row);
        if (value) counts.set(value, (counts.get(value) || 0) + 1);
      });
      return [...counts.entries()].sort((left, right) => String(left[0]).localeCompare(String(right[0]), 'fr', { numeric: true }));
    };
    return {
      sites: collect((row) => row.siteLabel),
      customers: collect((row) => row.customerName),
      requestTypes: collect((row) => row.requestTypeLabel),
      suppliers: collect((row) => row.supplier),
      brands: collect((row) => row.brand),
      models: collect((row) => row.model),
      replacementParts: collect((row) => row.replacementPart),
      statuses: collect((row) => row.statusLabel),
    };
  }, [documentsOnly, gridRows, showClosed]);

  const statusCardRows = useMemo(
    () =>
      (siteType === 'store' ? storeStatusCards : logisticsStatusCards).map((card) => ({
        ...card,
        // Les clôturés ne sont pas tous chargés : le compteur vient du serveur.
        count: card.key === 'closed'
          ? savListMeta.closedCount
          : gridRows.filter((row) => row.displayStatus === card.key).length,
        label: getOptionLabel(statusOptions, card.key),
      })),
    [gridRows, savListMeta.closedCount, siteType]
  );

  const statusCardSections = useMemo(() => {
    if (siteType === 'store') return [{ title: null, cards: statusCardRows }];

    return logisticsCardSections
      .map((section) => ({
        ...section,
        cards: section.keys.map((key) => statusCardRows.find((card) => card.key === key)).filter(Boolean),
      }))
      .filter((section) => section.cards.length);
  }, [siteType, statusCardRows]);

  const overdueOrderedRows = useMemo(
    () =>
      isLogisticsContext
        ? gridRows.filter((row) => row.status === 'ordered' && row.blockedDays > ORDERED_STATUS_ALERT_DAYS)
        : [],
    [gridRows, isLogisticsContext]
  );

  const alertCardRows = useMemo(() => {
    if (!isLogisticsContext) return [];

    return logisticsAlertCards.map((card) => ({
      ...card,
      count: gridRows.filter((row) => {
        if (card.key === 'late_store_response') {
          return row.status === 'waiting_store_info' && row.blockedDays > 5;
        }
        if (card.key === 'late_supplier_response') {
          return row.status === 'supplier_order' && row.blockedDays > 5;
        }
        return row.displayStatus === card.key;
      }).length,
    }));
  }, [gridRows, isLogisticsContext]);

  const handleStatusCardClick = (statusLabel) => {
    if (statusLabel === getOptionLabel(statusOptions, 'closed') && !showClosed) setShowClosed(true);
    setAlertFilter('');
    setColumnFilters((current) => ({
      ...current,
      status: current.status.length === 1 && current.status[0] === statusLabel ? [] : [statusLabel],
    }));
  };

  const handleAlertCardClick = (key) => {
    setColumnFilters((current) => ({ ...current, status: [] }));
    setAlertFilter((current) => (current === key ? '' : key));
  };

  const filteredRows = useMemo(() => {
    const normalizedQuickFilter = quickFilter.trim().toLowerCase();
    return gridRows.filter(
      (row) => {
        if (!documentsOnly && !showClosed && ['closed', 'cancelled'].includes(row.status)) return false;
        const matchesAlert =
          !alertFilter ||
          (alertFilter === 'late_store_response' && row.status === 'waiting_store_info' && row.blockedDays > 5) ||
          (alertFilter === 'late_supplier_response' && row.status === 'supplier_order' && row.blockedDays > 5) ||
          (alertFilter === 'supplier_followup' && row.displayStatus === 'supplier_followup');

        return matchesAlert && (
        (!normalizedQuickFilter ||
          [
            row.reference,
            row.siteLabel,
            row.advisor,
            row.defect,
            row.replacementPart,
            row.supplier,
            row.brand,
            row.model,
            row.customerName,
            row.requestTypeLabel,
            row.statusLabel,
            row.comment,
            ...(row.attachments || []).map((attachment) => attachment.originalName),
          ]
            .join(' ')
            .toLowerCase()
            .includes(normalizedQuickFilter)) &&
        matchesSelection(row.siteLabel, columnFilters.site) &&
        matchesSelection(row.customerName, columnFilters.customer) &&
        matchesSelection(row.requestTypeLabel, columnFilters.requestType) &&
        matchesSelection(row.supplier, columnFilters.supplier) &&
        matchesSelection(row.brand, columnFilters.brand) &&
        matchesSelection(row.model, columnFilters.model) &&
        matchesSelection(row.replacementPart, columnFilters.replacementPart) &&
        matchesSelection(row.statusLabel, columnFilters.status)
        );
      }
    );
  }, [alertFilter, gridRows, quickFilter, columnFilters, documentsOnly, showClosed]);

  useEffect(() => {
    setSavTablePage(0);
  }, [alertFilter, columnFilters, quickFilter, savSort, showClosed]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredRows.length / savTablePageSize) - 1);
    setSavTablePage((current) => Math.min(current, lastPage));
  }, [filteredRows.length, savTablePageSize]);

  // Pagination d'affichage pour toutes les vues : les cartes, alertes et
  // filtres restent calculés sur l'ensemble des dossiers chargés.
  const sortedRows = useMemo(
    () => [...filteredRows].sort((left, right) => compareSavRows(left, right, savSort)),
    [filteredRows, savSort]
  );
  const tableRows = useMemo(
    () => sortedRows.slice(savTablePage * savTablePageSize, (savTablePage + 1) * savTablePageSize),
    [sortedRows, savTablePage, savTablePageSize]
  );
  // Premier clic : ordre croissant pour le texte, plus récent d'abord pour les dates.
  const toggleSavSort = (field) => {
    setSavSort((current) => {
      if (current.field === field) return { field, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      return { field, direction: ['createdAt', 'updatedAt'].includes(field) ? 'desc' : 'asc' };
    });
  };
  const renderSavSortLabel = (field, label) => (
    <TableSortLabel
      active={savSort.field === field}
      direction={savSort.field === field ? savSort.direction : 'asc'}
      onClick={() => toggleSavSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  // The SAV table can contain up to 500 populated rows. While a create/reply
  // dialog is open, typing in a controlled field used to rerender every row
  // behind the dialog on every keystroke. Keep the table shell mounted, but
  // skip that expensive row tree until the dialog is closed.
  const savDialogOpen = Boolean(
    open
    || detailTarget
    || statusTarget
    || supplierOrderTarget
    || bulkStatusOpen
    || documentTarget
    || preview.open
    || deleteTarget
  );
  const tableRowsToRender = savDialogOpen ? [] : tableRows;

  const bulkEligibleSavRows = useMemo(
    () => (isLogisticsContext ? filteredRows.filter((row) => canUpdateStatus(row)) : []),
    [canUpdateStatus, filteredRows, isLogisticsContext]
  );
  const selectedSavRows = useMemo(
    () => bulkEligibleSavRows.filter((row) => selectedSavIds.includes(row._id)),
    [bulkEligibleSavRows, selectedSavIds]
  );
  const toggleSelectedSav = (id) => {
    setSelectedSavIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };
  // La case d'en-tête ne (dé)sélectionne que les dossiers de la page affichée ;
  // une sélection faite sur d'autres pages est conservée.
  const pageBulkEligibleSavRows = useMemo(
    () => (isLogisticsContext ? tableRows.filter((row) => canUpdateStatus(row)) : []),
    [canUpdateStatus, isLogisticsContext, tableRows]
  );
  const toggleAllSelectedSav = () => {
    const ids = pageBulkEligibleSavRows.map((row) => row._id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedSavIds.includes(id));
    setSelectedSavIds((current) => allSelected ? current.filter((id) => !ids.includes(id)) : [...new Set([...current, ...ids])]);
  };
  const openBulkStatusDialog = () => {
    setBulkStatusForm({ status: selectedSavRows[0]?.status || 'new', comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
    setBulkStatusError('');
    setBulkStatusOpen(true);
  };
  const handleBulkStatusSubmit = async (event) => {
    event.preventDefault();
    if (!selectedSavRows.length) return;
    if (!bulkStatusForm.comment.trim() && bulkStatusForm.status !== 'ordered') {
      setBulkStatusError('Ajoute un commentaire pour le changement de statut.');
      return;
    }
    if (bulkStatusForm.status === 'returned_store' && !bulkStatusForm.cosiumDeliveryNoteNumber?.trim()) {
      setBulkStatusError('Saisis le numéro de BL Cosium avant d’envoyer les dossiers au magasin.');
      return;
    }
    if (bulkStatusForm.status === 'ordered' && !bulkStatusForm.supplierOrderNumber?.trim()) {
      setBulkStatusError('Saisis le numéro de commande fournisseur avant de passer les dossiers à Commandé.');
      return;
    }

    try {
      setBulkStatusSaving(true);
      setBulkStatusError('');
      const updates = await Promise.allSettled(
        selectedSavRows.map((row) =>
          requestJson(`/sav-requests/${row._id}/status`, {
            method: 'PATCH',
            body: JSON.stringify(stripEmptyValues(bulkStatusForm)),
          })
        )
      );
      const updatedRows = updates.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      const failedCount = updates.length - updatedRows.length;
      setRows((current) => current.map((row) => updatedRows.find((updated) => updated._id === row._id) || row));
      setSelectedSavIds([]);
      setBulkStatusOpen(false);
      if (failedCount) {
        setError(`${updatedRows.length} dossier(s) mis à jour, ${failedCount} en échec.`);
      }
      window.dispatchEvent(new Event('opticdev:logistics-updated'));
    } catch (err) {
      setBulkStatusError(err.message);
    } finally {
      setBulkStatusSaving(false);
    }
  };

  const resetFilters = () => {
    setQuickFilter('');
    setAlertFilter('');
    setShowClosed(false);
    setSavTablePage(0);
    setColumnFilters(EMPTY_COLUMN_FILTERS);
  };

  const filterFields = [
    ['site', 'Magasin', <StoreRoundedIcon fontSize="small" />, filterOptions.sites],
    ['customer', 'Client', <PersonRoundedIcon fontSize="small" />, filterOptions.customers],
    ['requestType', 'Type', <CategoryRoundedIcon fontSize="small" />, filterOptions.requestTypes],
    ['supplier', 'Fournisseur', <FactoryRoundedIcon fontSize="small" />, filterOptions.suppliers],
    ['brand', 'Marque', <SellRoundedIcon fontSize="small" />, filterOptions.brands],
    ['model', 'Modèle', <LabelRoundedIcon fontSize="small" />, filterOptions.models],
    ['replacementPart', 'Pièce', <BuildRoundedIcon fontSize="small" />, filterOptions.replacementParts],
    ['status', 'Statut', <FlagRoundedIcon fontSize="small" />, filterOptions.statuses],
  ].filter(([key]) => key !== 'site' || siteType !== 'store');
  const hasActiveFilters = Boolean(quickFilter.trim()) || Boolean(alertFilter) || showClosed ||
    Object.values(columnFilters).some((values) => values.length);

  const documentRows = useMemo(
    () =>
      filteredRows.flatMap((row) =>
        (row.attachments || []).map((attachment) => ({
          id: `${row._id}-${attachment._id}`,
          savRow: row,
          attachment,
          reference: row.reference,
          shortReference: row.shortReference,
          fileName: attachment.originalName || attachment.fileName || 'Document',
          mimeType: attachment.mimeType || '—',
          sizeLabel: formatFileSize(attachment.size),
          siteLabel: row.siteLabel,
          customerName: row.customerName,
          brand: row.brand,
          model: row.model,
          uploadedDate: attachment.uploadedAt ? new Date(attachment.uploadedAt).toLocaleDateString('fr-FR') : '',
        }))
      ),
    [filteredRows]
  );

  const handleOpenCreate = () => {
    setForm({
      ...emptyForm,
      siteId: siteType === 'store' ? site?._id || '' : '',
      savSiteId: siteType === 'sav' ? site?._id || '' : defaultSavSiteId,
    });
    setAttachmentFiles([]);
    setFormError('');
    setEditingId(null);
    setOpen(true);
  };

  const handleOpenEdit = useCallback((row) => {
    setForm({
      siteId: getId(row.siteId),
      savSiteId: getId(row.savSiteId),
      advisorId: getId(row.advisorId),
      advisor: row.advisor || '',
      requestType: row.requestType || 'sav',
      defect: row.defect || '',
      replacementPart: row.replacementPart || '',
      supplier: row.supplier || '',
      brand: row.brand || '',
      model: row.model || '',
      size: row.size || '',
      color: row.color || '',
      serialNumber: row.serialNumber || '',
      customerId: getId(row.customerId),
      customerName: row.customerName || '',
      customerEmail: row.customerEmail || '',
      customerPhone: row.customerPhone || '',
      warrantyStatus: legacyWarrantyLabels[row.warrantyStatus] || row.warrantyStatus || 'Garantie fournisseur',
      status: row.status || 'new',
      comment: row.comment || '',
    });
    setAttachmentFiles([]);
    setFormError('');
    setEditingId(row._id);
    setOpen(true);
  }, []);

  const openCloseDialog = (row) => {
    setStatusTarget(row);
    setStatusForm({ status: 'closed', comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
    setStatusError('');
    setStatusAttachmentFiles([]);
  };

  const handleOpenDetail = useCallback((row, mode = 'details') => {
    setDetailTarget(row);
    setDetailMode(mode);
    if (mode === 'history') {
      setStatusTarget(row);
      setStatusForm({ status: getDefaultStatusUpdateValue(row), comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: row.cosiumDeliveryNoteNumber || '' });
    } else {
      setStatusTarget(null);
      setStatusForm({ status: 'new', comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
    }
    setStatusAttachmentFiles([]);
    setStatusError('');
    setError('');
    if (isLogisticsContext && Number(row.unreadStoreReplyCount || 0) > 0) {
      requestJson(`/sav-requests/${row._id}/store-replies/read`, { method: 'PATCH', body: JSON.stringify({}) })
        .then((updated) => {
          setRows((current) => current.map((item) => (item._id === updated._id ? updated : item)));
          setDetailTarget((current) => (current?._id === updated._id ? updated : current));
          setStatusTarget((current) => (current?._id === updated._id ? updated : current));
        })
        .catch((err) => setError(err.message));
    }
  }, [getDefaultStatusUpdateValue, isLogisticsContext]);

  useEffect(() => {
    const requestedId = searchParams.get('open') || '';
    if (!requestedId || loading) return;

    const requestedMode = searchParams.get('mode') === 'history' ? 'history' : 'details';
    const requestKey = `${requestedId}:${requestedMode}`;
    if (openedSearchResultRef.current === requestKey) return;

    const requestedRow = gridRows.find((row) => String(row._id || row.id) === requestedId);
    if (!requestedRow) {
      if (requestedFetchRef.current === requestKey) return;
      requestedFetchRef.current = requestKey;
      requestJson(`/sav-requests/${encodeURIComponent(requestedId)}`)
        .then((item) => {
          setRows((current) => (
            current.some((row) => String(row._id || row.id) === requestedId)
              ? current
              : [item, ...current]
          ));
        })
        .catch((err) => setError(err.message));
      return;
    }

    openedSearchResultRef.current = requestKey;
    handleOpenDetail(requestedRow, requestedMode);
  }, [gridRows, handleOpenDetail, loading, searchParams]);

  const handleOpenStatusDialog = useCallback((row) => {
    setStatusTarget(row);
    setStatusForm({ status: getDefaultStatusUpdateValue(row), comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: row.cosiumDeliveryNoteNumber || '' });
    setStatusAttachmentFiles([]);
    setStatusError('');
    setError('');
  }, [getDefaultStatusUpdateValue]);

  const handleOpenSupplierOrderDialog = useCallback((row) => {
    setSupplierOrderTarget(row);
    setSupplierOrderForm({ supplierOrderNumber: row.supplierOrderNumber || '', comment: '' });
    setSupplierOrderError('');
  }, []);

  const handleSupplierOrderSubmit = async (event) => {
    event.preventDefault();
    if (!supplierOrderTarget) return;
    if (!supplierOrderForm.supplierOrderNumber.trim()) {
      setSupplierOrderError('Saisis le numéro de commande fournisseur.');
      return;
    }
    try {
      setSupplierOrderSaving(true);
      setSupplierOrderError('');
      const updated = await requestJson(`/sav-requests/${supplierOrderTarget._id}/supplier-order-number`, {
        method: 'PATCH',
        body: JSON.stringify(stripEmptyValues(supplierOrderForm)),
      });
      setRows((current) => current.map((row) => (row._id === updated._id ? updated : row)));
      setDetailTarget((current) => (current?._id === updated._id ? updated : current));
      setSupplierOrderTarget(null);
      setSupplierOrderForm({ supplierOrderNumber: '', comment: '' });
      window.dispatchEvent(new Event('opticdev:logistics-updated'));
    } catch (err) {
      setSupplierOrderError(err.message);
    } finally {
      setSupplierOrderSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'siteId' ? { advisorId: '', advisor: '' } : {}),
    }));
  };

  const setFieldValue = (name, value) => {
    setForm((current) => ({ ...current, [name]: value || '' }));
  };

  const buildPayload = () => {
    const enteredCustomerName = String(form.customerName || '').trim();
    const selectedCustomerName = selectedCustomer ? getCustomerLabel(selectedCustomer).trim() : '';
    const customerMatchesInput = Boolean(
      selectedCustomer && enteredCustomerName &&
      selectedCustomerName.localeCompare(enteredCustomerName, undefined, { sensitivity: 'base' }) === 0
    );
    return stripEmptyValues({
      ...form,
      // Une édition de dossier ne doit jamais changer son statut. Les
      // transitions passent exclusivement par la fenêtre de statut dédiée.
      ...(editingId ? { status: undefined } : {}),
      // Ne jamais rattacher silencieusement un ancien client si le texte saisi
      // ne correspond pas à celui sélectionné dans l'Autocomplete.
      customerId: customerMatchesInput ? form.customerId : '',
      advisor: form.advisor || (selectedAdvisor ? getUserLabel(selectedAdvisor) : ''),
      customerName: enteredCustomerName,
    });
  };

  const handleAttachmentChange = (event, requirementType = 'other') => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const categorizedFiles = files.map((file) => ({ file, requirementType }));
    const nextFiles = [...attachmentFiles, ...categorizedFiles].slice(0, MAX_ATTACHMENT_COUNT);
    const sizeError = nextFiles.map((entry) => getAttachmentSizeError(entry.file)).find(Boolean);
    if (sizeError) {
      setFormError(sizeError);
      return;
    }
    const exceedsMaxCount = attachmentFiles.length + files.length > MAX_ATTACHMENT_COUNT;
    if (exceedsMaxCount) {
      setFormError(`Tu peux ajouter ${MAX_ATTACHMENT_COUNT} fichiers maximum par enregistrement.`);
    } else {
      setFormError('');
    }
    setAttachmentFiles(nextFiles);
  };

  const handleStatusAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const nextFiles = [...statusAttachmentFiles, ...files].slice(0, MAX_ATTACHMENT_COUNT);
    const sizeError = nextFiles.map(getAttachmentSizeError).find(Boolean);
    if (sizeError) {
      setStatusError(sizeError);
      return;
    }
    if (statusAttachmentFiles.length + files.length > MAX_ATTACHMENT_COUNT) {
      setStatusError(`Tu peux ajouter ${MAX_ATTACHMENT_COUNT} fichiers maximum par mise à jour.`);
    } else {
      setStatusError('');
    }
    setStatusAttachmentFiles(nextFiles);
  };

  const removeSelectedAttachment = (index) => {
    setAttachmentFiles((current) => current.filter((_file, fileIndex) => fileIndex !== index));
  };

  const removeSelectedStatusAttachment = (index) => {
    setStatusAttachmentFiles((current) => current.filter((_file, fileIndex) => fileIndex !== index));
  };

  const buildSubmitOptions = () => {
    const payload = buildPayload();
    if (!attachmentFiles.length) {
      return { body: JSON.stringify(payload) };
    }

    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    attachmentFiles.forEach((entry) => formData.append('attachments', entry.file));
    formData.append('attachmentTypes', JSON.stringify(attachmentFiles.map((entry) => entry.requirementType)));
    return { body: formData };
  };

  const buildUpdatePayloadFromRow = (row) =>
    stripEmptyValues({
      siteId: getId(row.siteId),
      savSiteId: getId(row.savSiteId),
      advisorId: getId(row.advisorId),
      advisor: row.advisor,
      requestType: row.requestType || 'sav',
      defect: row.defect,
      replacementPart: row.replacementPart,
      supplier: row.supplier,
      brand: row.brand,
      model: row.model,
      size: row.size,
      color: row.color,
      serialNumber: row.serialNumber,
      customerId: getId(row.customerId),
      customerName: row.customerName,
      customerEmail: row.customerEmail,
      customerPhone: row.customerPhone,
      warrantyStatus: row.warrantyStatus,
      cosiumDeliveryNoteNumber: row.cosiumDeliveryNoteNumber,
      status: row.status,
      comment: row.comment,
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (blockedCompleteFrameSav) {
      setFormError(blockedCompleteFrameMessage);
      return;
    }
    if (serialNumberRequired && !form.serialNumber.trim()) {
      setFormError('Le numéro de série est obligatoire pour ce fournisseur ou cette marque.');
      return;
    }
    if (!hasRequiredDefectVideo) {
      setFormError('Une vidéo du défaut est obligatoire pour ce fournisseur ou cette marque.');
      return;
    }
    if (missingRequiredAttachmentEntries.length) {
      setFormError(`Ajoute un fichier dans chaque rubrique obligatoire : ${missingRequiredAttachmentEntries.map((entry) => entry.label).join(' + ')}.`);
      return;
    }
    if (currentAttachmentCount < requiredAttachmentCount) {
      setFormError(
        requiredAttachmentLabels.length
          ? `Pour ${requiredAttachmentSources || 'ce dossier'}, pièce(s) jointe(s) obligatoire(s) : ${requiredAttachmentLabels.join(' + ')}.`
          : 'Ajoute au moins un fichier au dossier Logistique.'
      );
      return;
    }
    try {
      setSaving(true);
      setError('');
      setFormError('');
      await requestJson(editingId ? `/sav-requests/${editingId}` : '/sav-requests', {
        method: editingId ? 'PUT' : 'POST',
        ...buildSubmitOptions(),
      });
      setAttachmentFiles([]);
      setFormError('');
      setOpen(false);
      await loadRows();
      window.dispatchEvent(new Event('opticdev:logistics-updated'));
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAttachment = useCallback(async (row, attachment) => {
    try {
      setError('');
      const { blob } = await requestBlob(`/sav-requests/${row._id}/attachments/${attachment._id}`);
      const url = URL.createObjectURL(blob);
      setPreview((current) => {
        if (current.url) URL.revokeObjectURL(current.url);
        return {
          open: true,
          url,
          mimeType: attachment.mimeType || blob.type || '',
          title: attachment.originalName || attachment.fileName || 'Document Logistique',
        };
      });
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const handleDownloadAttachment = useCallback(async (row, attachment) => {
    try {
      setError('');
      const { blob } = await requestBlob(`/sav-requests/${row._id}/attachments/${attachment._id}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalName || attachment.fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const handlePrintSavRequest = useCallback((row) => {
    const reference = getSavReference(row);
    const printWindow = window.open('', '_blank', 'width=900,height=760');
    if (!printWindow) {
      setError('Impossible d’ouvrir la fenêtre d’impression. Autorise les fenêtres pop-up pour imprimer le dossier.');
      return;
    }

    const renderFields = (fields) => fields
      .map(([label, value]) => `<div class="field"><span>${escapePrintHtml(label)}</span><strong>${escapePrintHtml(value || '—')}</strong></div>`)
      .join('');
    const storeDetails = renderFields([
      ['Magasin', row.siteLabel || getSiteCodeLabel(row.siteId)],
      ['Site Logistique', getSiteLabel(row.savSiteId)],
      ['Conseiller', row.advisor || getUserLabel(row.advisorId)],
      ['Création', formatDateTime(row.createdAt)],
    ]);
    const customerDetails = renderFields([
      ['Client', row.customerName],
      ['Type de demande', row.requestTypeLabel || getOptionLabel(requestTypeOptions, row.requestType || 'sav')],
      ['Mail client', row.customerEmail],
      ['Téléphone client', row.customerPhone],
      ['Garantie', row.warrantyLabel || row.warrantyStatus],
    ]);
    const requestDetails = renderFields([
      ['Défaut', row.defect],
      ['Pièce à remplacer', row.replacementPart],
      ['Fournisseur', row.supplier],
      ['N° commande fournisseur', row.supplierOrderNumber],
      ['N° BL Cosium', row.cosiumDeliveryNoteNumber],
      ['Marque', row.brand],
      ['Modèle', row.model],
      ['Taille / coloris', [row.size, row.color].filter(Boolean).join(' / ')],
    ]);

    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${escapePrintHtml(reference)}</title><style>
      @page { margin: 12mm; } body { font-family: Arial, sans-serif; color: #111827; margin: 0; font-size: 12px; }
      .print-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 16px; }
      h1 { margin: 0; font-size: 23px; } .sub { margin: 5px 0 0; color: #475569; font-size: 12px; }
      .client-name { max-width: 45%; text-align: right; font-size: 20px; line-height: 1.2; font-weight: 800; overflow-wrap: anywhere; }
      .barcode { width: 100%; height: 68px; margin: 0 0 16px; border: 1px solid #cbd5e1; border-radius: 6px; padding: 6px; box-sizing: border-box; }
      .barcode svg { width: 100%; height: 100%; } .top-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
      .section { border: 1px solid #cbd5e1; border-radius: 8px; padding: 14px; break-inside: avoid; } .section.request { margin-top: 12px; }
      h2 { margin: 0 0 12px; font-size: 15px; } .fields { display: grid; grid-template-columns: 1fr; gap: 10px; } .request .fields { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
      .field { min-height: 30px; } .field span { display: block; color: #64748b; font-size: 10px; margin-bottom: 3px; font-weight: 700; } .field strong { font-size: 12px; line-height: 1.3; }
      .comment { margin-top: 14px; border-top: 1px solid #cbd5e1; padding-top: 12px; white-space: pre-wrap; } .comment span { display: block; color: #64748b; font-size: 10px; margin-bottom: 5px; font-weight: 700; }
    </style></head><body><div class="print-header"><div><h1>${escapePrintHtml(reference)}</h1><p class="sub">Dossier Logistique · ${escapePrintHtml(row.statusLabel || getOptionLabel(statusOptions, row.status))}</p></div><div class="client-name">${escapePrintHtml(row.customerName || 'Client non renseigné')}</div></div><div class="barcode">${getSavBarcodeSvg(reference)}</div><div class="top-grid"><section class="section"><h2>Informations magasin</h2><div class="fields">${storeDetails}</div></section><section class="section"><h2>Informations client</h2><div class="fields">${customerDetails}</div></section></div><section class="section request"><h2>Demande</h2><div class="fields">${requestDetails}</div>${row.comment ? `<div class="comment"><span>Commentaire initial</span>${escapePrintHtml(row.comment)}</div>` : ''}</section><script>window.onload = () => window.print();</script></body></html>`);
    printWindow.document.close();
  }, []);

  const handleClosePreview = () => {
    setPreview((current) => {
      if (current.url) URL.revokeObjectURL(current.url);
      return { open: false, url: '', mimeType: '', title: '' };
    });
  };

  const handleStatusSubmit = async (event) => {
    event.preventDefault();
    if (!statusTarget) return;
    if (!statusForm.comment.trim() && statusForm.status !== 'ordered') {
      setStatusError('Ajoute un commentaire pour le changement de statut.');
      return;
    }
    if (statusForm.status === 'returned_store' && !statusForm.cosiumDeliveryNoteNumber?.trim()) {
      setStatusError('Saisis le numéro de BL Cosium avant d’envoyer le dossier au magasin.');
      return;
    }
    if (statusForm.status === 'ordered' && !statusForm.supplierOrderNumber?.trim()) {
      setStatusError('Saisis le numéro de commande fournisseur avant de passer le dossier à Commandé.');
      return;
    }

    try {
      setStatusSaving(true);
      setError('');
      setStatusError('');
      let updated = await requestJson(`/sav-requests/${statusTarget._id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(stripEmptyValues(statusForm)),
      });
      if (statusAttachmentFiles.length) {
        const formData = new FormData();
        formData.append('payload', JSON.stringify(buildUpdatePayloadFromRow(updated)));
        statusAttachmentFiles.forEach((file) => formData.append('attachments', file));
        updated = await requestJson(`/sav-requests/${updated._id}`, {
          method: 'PUT',
          body: formData,
        });
      }
      setRows((current) => current.map((row) => (row._id === updated._id ? updated : row)));
      if (editingId === updated._id) {
        setForm((current) => ({ ...current, status: updated.status }));
      }
      setStatusError('');
      if (statusForm.status === 'ordered') {
        setDetailTarget(null);
        setStatusTarget(null);
        setStatusForm({ status: 'new', comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
      } else if (detailTarget?._id === updated._id) {
        setDetailTarget(updated);
        setStatusTarget(updated);
        setStatusForm({ status: getDefaultStatusUpdateValue(updated), comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
      } else {
        setStatusTarget(null);
        setStatusForm({ status: 'new', comment: '', supplierOrderNumber: '', cosiumDeliveryNoteNumber: '' });
      }
      setStatusAttachmentFiles([]);
      window.dispatchEvent(new Event('opticdev:logistics-updated'));
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        setError('');
        await requestJson(`/sav-requests/${id}`, { method: 'DELETE' });
        setDeleteTarget(null);
        await loadRows();
        window.dispatchEvent(new Event('opticdev:logistics-updated'));
      } catch (err) {
        setError(err.message);
      }
    },
    [loadRows]
  );

  const toggleExpandedRow = useCallback((id) => {
    setExpandedRows((current) => ({ ...current, [id]: !current[id] }));
  }, []);

  const documentColumns = useMemo(
    () => [
      {
        field: 'fileName',
        headerName: 'Document',
        minWidth: 260,
        flex: 1.35,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <AttachFileIcon fontSize="small" color="primary" />
            <Typography variant="body2" noWrap title={params.value} sx={{ fontWeight: 800 }}>
              {params.value}
            </Typography>
          </Stack>
        ),
      },
      { field: 'shortReference', headerName: 'N° SAV', minWidth: 150, flex: 0.65 },
      { field: 'siteLabel', headerName: 'Magasin', minWidth: 180, flex: 0.9 },
      { field: 'customerName', headerName: 'Client', minWidth: 160, flex: 0.85 },
      { field: 'brand', headerName: 'Marque', minWidth: 130, flex: 0.65 },
      { field: 'model', headerName: 'Modèle', minWidth: 160, flex: 0.8 },
      { field: 'sizeLabel', headerName: 'Taille', minWidth: 95, flex: 0.45 },
      { field: 'uploadedDate', headerName: 'Ajouté le', minWidth: 115, flex: 0.55 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: isAdmin || siteType === 'store' ? 150 : 108,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ width: '100%' }}>
            <IconButton size="small" onClick={() => handleOpenAttachment(params.row.savRow, params.row.attachment)} aria-label="Voir le document">
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDownloadAttachment(params.row.savRow, params.row.attachment)} aria-label="Télécharger le document">
              <DownloadIcon fontSize="small" />
            </IconButton>
            {canEditRequest(params.row.savRow) && (
              <IconButton size="small" onClick={() => handleOpenEdit(params.row.savRow)} aria-label="Modifier le dossier">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ),
      },
    ],
    [canEditRequest, handleDownloadAttachment, handleOpenAttachment, handleOpenEdit, isAdmin, siteType]
  );

  const handleLogisticsPortalSubmit = async ({ login: collaboratorLogin, password }) => {
    try {
      setPortalLoading(true);
      setPortalError('');
      const payload = await login({
        logisticsUser: true,
        login: collaboratorLogin,
        password,
      });
      if (payload.site) {
        setSession({
          mode: 'site',
          site: payload.site,
          permissions: payload.permissions || [],
          user: payload.account || null,
        });
      }
    } catch (err) {
      setPortalError(err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
          {menuPage.sectionTitle}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {menuPage.pageTitle}
        </Typography>
      </Box>

      {error && !open && !statusTarget && !preview.open && <Alert severity="error">{error}</Alert>}

      {!documentsOnly && (
        <Stack spacing={2.25}>
          {statusCardSections.map((section) => (
            <Stack key={section.title || 'store-cards'} spacing={0.9}>
              {section.title && (
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950, letterSpacing: '0.08em' }}>
                  {section.title}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: `repeat(${Math.min(section.cards.length, 2)}, minmax(0, 1fr))`,
                    xl: `repeat(${Math.min(section.cards.length, 4)}, minmax(0, 1fr))`,
                  },
                  gap: 1.5,
                }}
              >
                {section.cards.map((card) => {
                  const active = columnFilters.status.length === 1 && columnFilters.status[0] === card.label;
                  return (
                    <Card
                      key={card.key}
                      elevation={0}
                      onClick={() => handleStatusCardClick(card.label)}
                      sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: active ? card.color : 'divider',
                        bgcolor: card.bgcolor,
                        borderRadius: 2,
                        minHeight: 116,
                        transition: '160ms ease',
                        boxShadow: active ? '0 0 0 1px currentColor' : 'none',
                        color: active ? card.color : 'inherit',
                        '&:hover': { borderColor: card.color, transform: 'translateY(-1px)' },
                      }}
                    >
                      <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ height: '100%' }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                              {card.title}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.05, color: active ? card.color : 'text.primary' }}>
                              {card.count}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.7, lineHeight: 1.25 }}>
                              {card.helper}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              flex: '0 0 auto',
                              color: card.color,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              '& svg': { fontSize: 26 },
                            }}
                          >
                            {card.icon}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Stack>
          ))}
        </Stack>
      )}

      {!documentsOnly && isLogisticsContext && alertCardRows.length > 0 && (
        <Stack spacing={0.9}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950, letterSpacing: '0.08em' }}>
            Alertes
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {alertCardRows.map((card) => {
              const active = alertFilter === card.key;
              return (
                <Card
                  key={card.key}
                  elevation={0}
                  onClick={() => handleAlertCardClick(card.key)}
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: active ? card.color : 'divider',
                    bgcolor: card.bgcolor,
                    borderRadius: 2,
                    minHeight: 116,
                    transition: '160ms ease',
                    boxShadow: active ? '0 0 0 1px currentColor' : 'none',
                    color: active ? card.color : 'inherit',
                    '&:hover': { borderColor: card.color, transform: 'translateY(-1px)' },
                  }}
                >
                  <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                          {card.title}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.05, color: active ? card.color : 'text.primary' }}>
                          {card.count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.7, lineHeight: 1.25 }}>
                          {card.helper}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          flex: '0 0 auto',
                          color: card.color,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          '& svg': { fontSize: 26 },
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Stack>
      )}

      {!documentsOnly && overdueOrderedRows.length > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setColumnFilters((current) => ({ ...current, status: ['Commandé'] }))}
            >
              VOIR
            </Button>
          }
        >
          {overdueOrderedRows.length} dossier{overdueOrderedRows.length > 1 ? 's' : ''} commandé{overdueOrderedRows.length > 1 ? 's' : ''}{' '}
          sans changement depuis plus de 6 semaines.
        </Alert>
      )}

      {!documentsOnly && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Stack direction="row" justifyContent="center">
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ fontWeight: 850 }}>
                Nouveau SAV
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2 }}>
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterAltRoundedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Filtres
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" startIcon={<RestartAltRoundedIcon fontSize="small" />} onClick={resetFilters} disabled={!hasActiveFilters} sx={{ minHeight: 32 }}>
                Réinitialiser
              </Button>
            </Stack>
            <TextField
              value={quickFilter}
              onChange={(event) => setQuickFilter(event.target.value)}
              placeholder="Rechercher un N° SAV, un client, un magasin, une marque, un modèle, un commentaire…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {filterFields.map(([key, label, icon, options]) => (
                <FilterButton
                  key={key}
                  label={label}
                  icon={icon}
                  options={options}
                  selected={columnFilters[key]}
                  onChange={(values) => setColumnFilters((current) => ({ ...current, [key]: values }))}
                />
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <ToggleButtonGroup
                exclusive
                color="primary"
                value={showClosed ? 'all' : 'open'}
                onChange={(_event, value) => { if (value) setShowClosed(value === 'all'); }}
                aria-label="Dossiers affichés"
                sx={{ '& .MuiToggleButton-root': { px: 2, py: 0.75, fontWeight: 850, textTransform: 'none', gap: 0.75 } }}
              >
                <ToggleButton value="open">
                  <PendingActionsRoundedIcon fontSize="small" />
                  Dossiers en cours
                  <Chip size="small" label={openSavCount} color={showClosed ? 'default' : 'primary'} sx={{ height: 20, fontWeight: 900 }} />
                </ToggleButton>
                <ToggleButton value="all">
                  <TaskAltRoundedIcon fontSize="small" />
                  Inclure clôturés et annulés
                  <Chip size="small" label={`+${savListMeta.closedCount + savListMeta.cancelledCount}`} color={showClosed ? 'primary' : 'default'} sx={{ height: 20, fontWeight: 900 }} />
                </ToggleButton>
              </ToggleButtonGroup>
              {showClosed && savListMeta.closedCount + savListMeta.cancelledCount > 500 && (
                <Typography variant="caption" color="text.secondary">
                  Les 500 dossiers clôturés/annulés les plus récents sont chargés.
                </Typography>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {!documentsOnly && (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" color="text.secondary">
                {filteredRows.length} dossier{filteredRows.length > 1 ? 's' : ''}
              </Typography>
              {isLogisticsContext && (
                <>
                  <Chip size="small" label={`${selectedSavRows.length} sélectionné(s)`} color={selectedSavRows.length ? 'primary' : 'default'} sx={{ fontWeight: 850 }} />
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<UpdateRoundedIcon fontSize="small" />}
                    disabled={!selectedSavRows.length}
                    onClick={openBulkStatusDialog}
                    sx={{ fontWeight: 850, textTransform: 'none' }}
                  >
                    Suivi multiple
                  </Button>
                </>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
                Rafraîchir
              </Button>
            </Stack>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900, '& td, & th': { px: 1 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ width: 44 }} />
                    {isLogisticsContext && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={pageBulkEligibleSavRows.length > 0 && pageBulkEligibleSavRows.every((row) => selectedSavIds.includes(row._id))}
                          indeterminate={
                            pageBulkEligibleSavRows.some((row) => selectedSavIds.includes(row._id)) &&
                            !pageBulkEligibleSavRows.every((row) => selectedSavIds.includes(row._id))
                          }
                          onChange={toggleAllSelectedSav}
                          inputProps={{ 'aria-label': 'Sélectionner tous les dossiers de la page' }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="left" sx={{ fontWeight: 950, whiteSpace: 'nowrap' }}>
                      Pièces / Actions
                    </TableCell>
                    <TableCell sx={{ fontWeight: 950, whiteSpace: 'nowrap' }}>
                      <Tooltip title="Trier par date de création">{renderSavSortLabel('createdAt', 'N° SAV')}</Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 950, whiteSpace: 'nowrap' }}>{renderSavSortLabel('supplier', 'Fournisseur / Marque')}</TableCell>
                    <TableCell sx={{ fontWeight: 950, whiteSpace: 'nowrap' }}>{renderSavSortLabel('customer', 'Client')}</TableCell>
                    <TableCell sx={{ fontWeight: 950, whiteSpace: 'nowrap' }}>
                      {renderSavSortLabel('status', 'Statut')}
                      <Typography component="span" color="text.disabled" sx={{ mx: 0.5 }}>/</Typography>
                      {renderSavSortLabel('updatedAt', 'Mise à jour')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 950, whiteSpace: 'nowrap' }}>{renderSavSortLabel('warranty', 'Garantie')}</TableCell>
                    <TableCell sx={{ fontWeight: 950, whiteSpace: 'nowrap' }}>
                      {siteType === 'store' ? renderSavSortLabel('advisor', 'Conseiller') : renderSavSortLabel('site', 'Magasin / Conseiller')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isLogisticsContext ? 9 : 8}>
                        <Alert severity="info">Aucun dossier ne correspond aux filtres.</Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableRowsToRender.map((row) => {
                      const expanded = Boolean(expandedRows[row.id]);
                      const storeInfoAnswer = canStoreAnswerInfo(row);
                      return (
                        <Fragment key={row.id}>
                          <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                            <TableCell>
                              <IconButton size="small" onClick={() => toggleExpandedRow(row.id)} aria-label={expanded ? 'Masquer les détails' : 'Afficher les détails'}>
                                {expanded ? <KeyboardArrowUpRoundedIcon fontSize="small" /> : <KeyboardArrowDownRoundedIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            {isLogisticsContext && (
                              <TableCell padding="checkbox">
                                {canUpdateStatus(row) && (
                                  <Checkbox
                                    size="small"
                                    checked={selectedSavIds.includes(row._id)}
                                    onChange={() => toggleSelectedSav(row._id)}
                                    inputProps={{ 'aria-label': `Sélectionner le dossier ${row.reference}` }}
                                  />
                                )}
                              </TableCell>
                            )}
                            <TableCell align="left">
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-start">
                                {isLogisticsContext && Number(row.unreadStoreReplyCount || 0) > 0 && (
                                  <Tooltip
                                    title={`${Number(row.unreadStoreReplyCount)} nouveau${Number(row.unreadStoreReplyCount) > 1 ? 'x' : ''} commentaire${Number(row.unreadStoreReplyCount) > 1 ? 's' : ''} magasin`}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        flex: '0 0 28px',
                                        borderRadius: '50%',
                                        display: 'inline-grid',
                                        placeItems: 'center',
                                        bgcolor: 'error.main',
                                        color: 'error.contrastText',
                                        fontSize: '0.78rem',
                                        fontWeight: 950,
                                        boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.18)',
                                      }}
                                    >
                                      {Math.min(99, Number(row.unreadStoreReplyCount))}
                                    </Box>
                                  </Tooltip>
                                )}
                                <Tooltip title={row.attachmentCount ? 'Voir les pièces jointes' : 'Aucune pièce jointe'}>
                                  <span>
                                    <Button
                                      size="small"
                                      variant={row.attachmentCount ? 'contained' : 'outlined'}
                                      startIcon={<AttachFileIcon fontSize="small" />}
                                      onClick={() => setDocumentTarget(row)}
                                      disabled={!row.attachmentCount}
                                      sx={{ minWidth: 58, fontWeight: 850 }}
                                    >
                                      {row.attachmentCount || 0}
                                    </Button>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Voir le détail">
                                  <IconButton size="small" onClick={() => handleOpenDetail(row, 'details')}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isLogisticsContext && row.status === 'supplier_confirmation' && (
                                  <Tooltip title="Saisir le N° de commande fournisseur">
                                    <IconButton size="small" onClick={() => handleOpenSupplierOrderDialog(row)} sx={{ ...ROW_ICON_BUTTON_SX, bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                      <AssignmentRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={getFollowupActionLabel(row, storeInfoAnswer)}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDetail(row, 'history')}
                                    aria-label={`${getFollowupActionLabel(row, storeInfoAnswer)} — ${row.reference}`}
                                    sx={canUpdateStatus(row)
                                      ? { ...ROW_ICON_BUTTON_SX, bgcolor: row.status === 'waiting_store_info' && storeInfoAnswer ? 'warning.main' : 'primary.main', '&:hover': { bgcolor: row.status === 'waiting_store_info' && storeInfoAnswer ? 'warning.dark' : 'primary.dark' } }
                                      : { color: 'primary.main' }}
                                  >
                                    <AddCommentRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {canStoreClose(row) && (
                                  <Tooltip title="Clôturer le dossier">
                                    <IconButton size="small" onClick={() => openCloseDialog(row)} sx={{ ...ROW_ICON_BUTTON_SX, bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
                                      <TaskAltRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Plus d’actions">
                                  <IconButton size="small" onClick={(event) => setRowMenu({ anchorEl: event.currentTarget, row })} aria-label={`Autres actions pour ${row.reference}`}>
                                    <MoreVertRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 850 }}>{row.reference}</Typography>
                              {row.createdAt && (
                                <Tooltip title={`Créé le ${formatDateTime(row.createdAt)}`}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Créé le {row.createdDate} · {getDaysSince(row.createdAt)} j
                                  </Typography>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 130 }}>
                              <Tooltip title="Fournisseur" placement="left">
                                <Stack direction="row" spacing={0.6} alignItems="center">
                                  <FactoryRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>{row.supplier || '—'}</Typography>
                                </Stack>
                              </Tooltip>
                              <Tooltip title="Marque" placement="left">
                                <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.25 }}>
                                  <SellRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.25 }}>{row.brand || '—'}</Typography>
                                </Stack>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <PersonCell name={row.customerName} variant="rounded" />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={row.statusLabel} color={getStatusColor(row.displayStatus || row.status)} />
                                {row.automaticallyClosedAt && (
                                  <Tooltip title={`Clôturé automatiquement le ${formatDateTime(row.automaticallyClosedAt)}`}>
                                    <Chip size="small" label="Auto" color="success" variant="outlined" />
                                  </Tooltip>
                                )}
                              </Stack>
                              {row.updatedAt && (
                                <Tooltip title={`Dernière mise à jour le ${formatDateTime(row.updatedAt)}`}>
                                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, color: 'text.primary', whiteSpace: 'nowrap' }}>
                                    <CalendarMonthRoundedIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                      Mise à jour {formatRelativeSince(row.updatedAt)}
                                    </Typography>
                                  </Stack>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={row.warrantyLabel || ''}>
                                <Chip
                                  size="small"
                                  label={getShortWarrantyLabel(row.warrantyLabel) || '—'}
                                  color={getWarrantyColor(row.warrantyLabel)}
                                  variant="outlined"
                                  sx={{ fontWeight: 850 }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                              {siteType !== 'store' && (
                                <Chip
                                  size="small"
                                  icon={<StoreRoundedIcon fontSize="small" />}
                                  label={row.siteLabel || '—'}
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 900, mb: 0.5 }}
                                />
                              )}
                              <PersonCell name={row.advisor} />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={isLogisticsContext ? 9 : 8} sx={{ p: 0, borderBottom: expanded ? '1px solid' : 0, borderColor: 'divider' }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                                      gap: 1.5,
                                    }}
                                  >
                                    <DetailField label="Marque" value={row.brand} />
                                    <DetailField label="Modèle" value={row.model} />
                                    <DetailField label="Taille" value={row.size} />
                                    <DetailField label="Coloris" value={row.color} />
                                    <DetailField label="N° de série" value={row.serialNumber} />
                                    <DetailField label="Pièce à changer" value={row.replacementPart} />
                                    <DetailField label="Défaut" value={row.defect} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                        Statut garantie
                                      </Typography>
                                      <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                          size="small"
                                          label={row.warrantyLabel || '—'}
                                          color={getWarrantyColor(row.warrantyLabel)}
                                          variant="outlined"
                                          sx={{ fontWeight: 850 }}
                                        />
                                      </Box>
                                    </Box>
                                    <DetailField label="Création" value={row.createdDate} />
                                    <DetailField label="Dernière mise à jour" value={row.updatedDate} />
                                    <DetailField label="N° commande fournisseur" value={row.supplierOrderNumber} />
                                    <DetailField label="N° BL Cosium" value={row.cosiumDeliveryNoteNumber} />
                                    <DetailField label="Commentaire" value={row.comment} />
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={savTablePage}
            onPageChange={(_event, page) => setSavTablePage(page)}
            rowsPerPage={savTablePageSize}
            onRowsPerPageChange={(event) => {
              setSavTablePageSize(Number(event.target.value));
              setSavTablePage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 150]}
            labelRowsPerPage="Dossiers par page"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
          />
          <Menu
            anchorEl={rowMenu?.anchorEl || null}
            open={Boolean(rowMenu)}
            onClose={() => setRowMenu(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {rowMenu && (
              <MenuItem onClick={() => { const { row } = rowMenu; setRowMenu(null); handleOpenDetail(row, 'history'); }}>
                <ListItemIcon><AddCommentRoundedIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText>{getFollowupActionLabel(rowMenu.row, canStoreAnswerInfo(rowMenu.row))}</ListItemText>
              </MenuItem>
            )}
            {rowMenu && canStoreClose(rowMenu.row) && (
              <MenuItem onClick={() => { const { row } = rowMenu; setRowMenu(null); openCloseDialog(row); }}>
                <ListItemIcon><TaskAltRoundedIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText>Clôturer le dossier</ListItemText>
              </MenuItem>
            )}
            {rowMenu && isLogisticsContext && rowMenu.row.status === 'supplier_confirmation' && (
              <MenuItem onClick={() => { const { row } = rowMenu; setRowMenu(null); handleOpenSupplierOrderDialog(row); }}>
                <ListItemIcon><AssignmentRoundedIcon fontSize="small" color="secondary" /></ListItemIcon>
                <ListItemText>Saisir le N° de commande</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => { const { row } = rowMenu; setRowMenu(null); handlePrintSavRequest(row); }}>
              <ListItemIcon><PrintRoundedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Imprimer le dossier</ListItemText>
            </MenuItem>
            {rowMenu && canEditRequest(rowMenu.row) && (
              <MenuItem onClick={() => { const { row } = rowMenu; setRowMenu(null); handleOpenEdit(row); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{isAdmin ? 'Modifier' : 'Modifier avant prise en charge'}</ListItemText>
              </MenuItem>
            )}
            {rowMenu && canDelete && (
              <MenuItem onClick={() => { const { row } = rowMenu; setRowMenu(null); setDeleteTarget(row); }} sx={{ color: 'error.main' }}>
                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>Supprimer</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </CardContent>
      </Card>
      )}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                Documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {documentRows.length} document{documentRows.length > 1 ? 's' : ''} lié{documentRows.length > 1 ? 's' : ''} aux dossiers filtrés
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
              Rafraîchir
            </Button>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 5 }}>
              <CircularProgress />
            </Stack>
          ) : documentRows.length === 0 ? (
            <Alert severity="info">Aucun document Logistique pour la sélection actuelle.</Alert>
          ) : (
            <DataGrid
              rows={documentRows}
              columns={documentColumns}
              autoHeight
              rowHeight={52}
              columnHeaderHeight={46}
              disableRowSelectionOnClick
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 25, 50]}
              sx={logisticsGridSx}
            />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => {
          setAttachmentFiles([]);
          setFormError('');
          setOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Modifier un dossier Logistique' : 'Créer une demande'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}

              <FormSection title="Informations magasin" icon={<StoreRoundedIcon />}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField name="siteId" label="Magasin" select value={form.siteId} onChange={handleChange} required fullWidth disabled={!canChooseStore}>
                    {storeSites.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {[item.code, getSiteLabel(item)].filter(Boolean).join(' - ')}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField name="savSiteId" label="Site Logistique" select value={form.savSiteId} onChange={handleChange} fullWidth disabled={!isAdmin}>
                    <MenuItem value="">Non assigné</MenuItem>
                    {savSites.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {[item.code, getSiteLabel(item)].filter(Boolean).join(' - ')}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    name="requestType"
                    label="Type de demande"
                    select
                    value={form.requestType || 'sav'}
                    onChange={handleChange}
                    required
                    fullWidth
                  >
                    {requestTypeOptions.map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Autocomplete
                    options={advisorOptions}
                    value={selectedAdvisor}
                    onChange={(_event, value) =>
                      setForm((current) => ({
                        ...current,
                        advisorId: value?._id || '',
                        advisor: value ? getUserLabel(value) : '',
                      }))
                    }
                    getOptionLabel={getUserLabel}
                    renderInput={(params) => <TextField {...params} label="Conseiller" />}
                    noOptionsText="Aucun utilisateur pour ce magasin"
                    fullWidth
                  />
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}
                >
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Statut
                    </Typography>
                    <Chip size="small" label={getOptionLabel(statusOptions, form.status)} color={getStatusColor(form.status)} />
                  </Stack>
                  {editingRow && isLogisticsContext && (
                    <Button size="small" variant="outlined" onClick={() => handleOpenStatusDialog(editingRow)}>
                      Modifier
                    </Button>
                  )}
                </Stack>
              </FormSection>

              <FormSection title="Informations client" icon={<PersonRoundedIcon />}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Autocomplete
                    freeSolo
                    options={customerOptions}
                    value={form.customerName || selectedCustomer || ''}
                    inputValue={form.customerName || ''}
                    onInputChange={(_event, value, reason) => {
                      if (reason === 'reset') return;
                      setForm((current) => ({
                        ...current,
                        customerId: '',
                        customerName: value || '',
                      }));
                    }}
                    onChange={(_event, value) =>
                      setForm((current) => {
                        if (value && typeof value === 'object') {
                          return {
                            ...current,
                            customerId: value._id || '',
                            customerName: getCustomerLabel(value),
                            customerEmail: value.email || '',
                            customerPhone: value.phone || '',
                          };
                        }
                        return {
                          ...current,
                          customerId: '',
                          customerName: value || current.customerName || '',
                        };
                      })
                    }
                    getOptionLabel={(option) => (typeof option === 'string' ? option : getCustomerLabel(option))}
                    isOptionEqualToValue={(option, value) => option?._id && option._id === value?._id}
                    filterOptions={(options, params) => {
                      const input = params.inputValue.trim().toLowerCase();
                      if (!input) return options.slice(0, 30);
                      return options
                        .filter((option) => getCustomerLabel(option).toLowerCase().includes(input))
                        .slice(0, 30);
                    }}
                    renderInput={(params) => <TextField {...params} label="Nom et prénom du client" required />}
                    noOptionsText="Aucun client trouvé, la saisie sera créée"
                    fullWidth
                  />
                  <Autocomplete
                    freeSolo
                    options={savSettings.warranties}
                    value={form.warrantyStatus}
                    onInputChange={(_event, value) => setFieldValue('warrantyStatus', value)}
                    onChange={(_event, value) => setFieldValue('warrantyStatus', value)}
                    renderInput={(params) => <TextField {...params} label="Garantie" />}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField name="customerEmail" label="Mail client" type="email" value={form.customerEmail} onChange={handleChange} fullWidth />
                  <TextField name="customerPhone" label="Téléphone client" value={form.customerPhone} onChange={handleChange} fullWidth />
                </Stack>
              </FormSection>

              <FormSection title="Demande" icon={<AssignmentRoundedIcon />}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Autocomplete
                    freeSolo
                    options={savSettings.defects}
                    value={form.defect}
                    onInputChange={(_event, value) => setFieldValue('defect', value)}
                    onChange={(_event, value) => setFieldValue('defect', value)}
                    renderInput={(params) => <TextField {...params} label="Défaut" required />}
                    fullWidth
                  />
                  <Autocomplete
                    freeSolo
                    options={savSettings.replacementParts}
                    value={form.replacementPart}
                    onInputChange={(_event, value) => setFieldValue('replacementPart', value)}
                    onChange={(_event, value) => setFieldValue('replacementPart', value)}
                    renderInput={(params) => <TextField {...params} label="Pièce à remplacer" required />}
                    fullWidth
                  />
                </Stack>
                <Autocomplete
                  freeSolo
                  options={savSettings.suppliers}
                  value={form.supplier}
                  onInputChange={(_event, value) => setFieldValue('supplier', value)}
                  onChange={(_event, value) => setFieldValue('supplier', value)}
                  renderInput={(params) => <TextField {...params} label="Fournisseur" required />}
                  fullWidth
                />
                {blockedCompleteFrameSav && (
                  <Alert severity="error">
                    {blockedCompleteFrameMessage}
                  </Alert>
                )}
                <Autocomplete
                  freeSolo
                  options={availableBrandOptions}
                  value={form.brand}
                  onInputChange={(_event, value) => setFieldValue('brand', value)}
                  onChange={(_event, value) => setFieldValue('brand', value)}
                  renderInput={(params) => <TextField {...params} label="Marque" required />}
                  fullWidth
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField name="model" label="Modèle" value={form.model} onChange={handleChange} required fullWidth />
                  <TextField name="size" label="Taille" value={form.size} onChange={handleChange} fullWidth />
                  <TextField name="color" label="Coloris" value={form.color} onChange={handleChange} fullWidth />
                </Stack>
                <TextField
                  name="serialNumber"
                  label={`Numéro de série${serialNumberRequired ? ' *' : ''}`}
                  value={form.serialNumber}
                  onChange={handleChange}
                  required={serialNumberRequired}
                  helperText={serialNumberRequired ? 'Obligatoire pour le fournisseur ou la marque sélectionné(e).' : 'Optionnel'}
                  fullWidth
                />
                <TextField name="comment" label="Commentaire" value={form.comment} onChange={handleChange} multiline minRows={3} fullWidth />
              </FormSection>

              <FormSection title="Fichiers" icon={<AttachFileIcon />}>
                {(form.supplier || form.brand) && (
                  <Alert severity={currentAttachmentCount >= requiredAttachmentCount && hasRequiredDefectVideo ? 'info' : 'warning'}>
                    {attachmentRequirementsExempt ? (
                      <>Aucune pièce jointe obligatoire pour une commande client à facturer, une garantie interne payante ou un dossier hors garantie.</>
                    ) : requiredAttachmentLabels.length ? (
                      <>
                        Pour <strong>{requiredAttachmentSources}</strong>, pièce(s) jointe(s) obligatoire(s) :{' '}
                        <strong>{requiredAttachmentLabels.join(' + ')}</strong>.
                      </>
                    ) : (
                      <>Au moins une pièce jointe est obligatoire pour ce dossier Logistique.</>
                    )}
                    {!attachmentRequirementsExempt && <> Fichier(s) présent(s) : {currentAttachmentCount}/{requiredAttachmentCount}.</>}
                  </Alert>
                )}
                <Stack spacing={1.25}>
                {!attachmentRequirementsExempt && requiredAttachmentEntries.map((requirement) => {
                  const assigned = assignedRequirementTypes.has(requirement.type);
                  const accept = requirement.type === 'defectVideoRequired'
                    ? 'video/*,.mp4,.mov,.m4v,.avi,.webm,.mkv,.3gp'
                    : ['fullFramePhoto', 'defectivePartPhoto'].includes(requirement.type)
                      ? 'image/*'
                      : 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt';
                  return (
                    <Stack
                      key={requirement.type}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.25}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      justifyContent="space-between"
                      sx={{ border: '1px solid', borderColor: assigned ? 'success.light' : 'warning.light', borderRadius: 1.5, p: 1.25, bgcolor: assigned ? 'success.50' : 'warning.50' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <AttachFileIcon color={assigned ? 'success' : 'warning'} fontSize="small" />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 900, textTransform: 'capitalize' }}>{requirement.label}</Typography>
                          <Typography variant="caption" color={assigned ? 'success.main' : 'warning.main'}>{assigned ? 'Fichier ajouté' : 'Fichier obligatoire manquant'}</Typography>
                        </Box>
                      </Stack>
                      <Button component="label" size="small" variant={assigned ? 'outlined' : 'contained'} startIcon={<AttachFileIcon />} sx={{ whiteSpace: 'nowrap' }}>
                        {assigned ? 'Ajouter un autre' : 'Ajouter'}
                        <input type="file" hidden accept={accept} onChange={(event) => handleAttachmentChange(event, requirement.type)} />
                      </Button>
                    </Stack>
                  );
                })}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} sx={{ alignSelf: { sm: 'flex-start' } }}>
                    Autres fichiers
                    <input type="file" hidden multiple accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.mp4,.mov,.m4v,.avi,.webm,.mkv,.3gp" onChange={(event) => handleAttachmentChange(event, 'other')} />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Maximum {MAX_ATTACHMENT_COUNT} fichiers par enregistrement, toutes rubriques confondues : 10 Mo par fichier, 50 Mo pour une vidéo.
                  </Typography>
                </Stack>

                {attachmentFiles.length > 0 && (
                  <Stack spacing={0.75}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Fichiers à ajouter
                    </Typography>
                    {attachmentFiles.map((entry, index) => {
                      const file = entry.file;
                      const categoryLabel = requiredAttachmentEntries.find((requirement) => requirement.type === entry.requirementType)?.label || 'Autre fichier';
                      return (
                      <Stack
                        key={`${file.name}-${file.lastModified}`}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.75 }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <AttachFileIcon fontSize="small" color="primary" />
                          <Typography variant="body2" noWrap title={file.name} sx={{ fontWeight: 750 }}>
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} Mo
                          </Typography>
                          <Chip size="small" label={categoryLabel} variant="outlined" />
                        </Stack>
                        <Button size="small" color="error" onClick={() => removeSelectedAttachment(index)}>
                          Retirer
                        </Button>
                      </Stack>
                      );
                    })}
                  </Stack>
                )}

                {editingRow?.attachments?.length > 0 && (
                  <Stack spacing={0.75}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Pièces jointes du dossier
                    </Typography>
                    {editingRow.attachments.map((attachment) => (
                      <Stack
                        key={attachment._id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.75 }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <AttachFileIcon fontSize="small" color="primary" />
                          <Typography variant="body2" noWrap title={attachment.originalName} sx={{ fontWeight: 750 }}>
                            {attachment.originalName || attachment.fileName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {attachment.size ? `${(attachment.size / 1024 / 1024).toFixed(2)} Mo` : ''}
                          </Typography>
                          <Chip size="small" label={getAttachmentRequirementLabel(attachment.requirementType)} variant="outlined" />
                        </Stack>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => handleOpenAttachment(editingRow, attachment)}
                        >
                          Voir
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}

                </Stack>
              </FormSection>

              {editingRow?.statusHistory?.length > 0 && (
                <FormSection title="Workflow" icon={<HistoryRoundedIcon />}>
                  <SavStatusWorkflowVertical row={editingRow} />
                </FormSection>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAttachmentFiles([]);
                setOpen(false);
              }}
            >
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(detailTarget)}
        onClose={() => {
          setDetailTarget(null);
          setDetailMode('details');
          setStatusTarget(null);
          setStatusError('');
          setStatusAttachmentFiles([]);
        }}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            bgcolor: 'background.default',
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 950 }} noWrap>
                {detailTarget ? `${getShortSavReference(detailTarget)} · ${detailTarget.customerName || 'Client non renseigné'}` : ''}
              </Typography>
              {detailTarget && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  Dossier Logistique
                </Typography>
              )}
            </Box>
            {detailTarget && (
              <Chip
                label={getOptionLabel(statusOptions, detailTarget.displayStatus || getDisplayLogisticsStatus(detailTarget, blockedThresholdDays))}
                color={getStatusColor(detailTarget.displayStatus || getDisplayLogisticsStatus(detailTarget, blockedThresholdDays))}
                sx={{ fontWeight: 850 }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, md: 2.5 } }}>
          {detailTarget && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: detailMode === 'history' ? 'minmax(0, 900px)' : '1fr',
                justifyContent: detailMode === 'history' ? 'center' : 'stretch',
                gap: 2.5,
                alignItems: 'start',
              }}
            >
              <Stack spacing={2.5}>
                {detailMode === 'details' && (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                    <DetailCardSection title="Informations magasin" icon={<StoreRoundedIcon />}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                        <DetailField label="Magasin" value={detailTarget.siteLabel || getSiteLabel(detailTarget.siteId)} />
                        <DetailField label="Site Logistique" value={getSiteLabel(detailTarget.savSiteId)} />
                        <DetailField label="Conseiller" value={detailTarget.advisor || getUserLabel(detailTarget.advisorId)} />
                        <DetailField label="Création" value={formatDateTime(detailTarget.createdAt)} />
                      </Box>
                    </DetailCardSection>

                    <DetailCardSection title="Informations client" icon={<PersonRoundedIcon />}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                        <DetailField label="Client" value={detailTarget.customerName} />
                        <DetailField label="Type de demande" value={detailTarget.requestTypeLabel || getOptionLabel(requestTypeOptions, detailTarget.requestType || 'sav')} />
                        <DetailField label="Mail client" value={detailTarget.customerEmail} />
                        <DetailField label="Téléphone client" value={detailTarget.customerPhone} />
                        <DetailField label="Garantie" value={detailTarget.warrantyLabel || detailTarget.warrantyStatus} />
                      </Box>
                    </DetailCardSection>

                    <DetailCardSection title="Demande" icon={<AssignmentRoundedIcon />} sx={{ gridColumn: { md: '1 / -1' } }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                        <DetailField label="Défaut" value={detailTarget.defect} />
                        <DetailField label="Pièce à remplacer" value={detailTarget.replacementPart} />
                        <DetailField label="Fournisseur" value={detailTarget.supplier} />
                        <DetailField label="N° commande fournisseur" value={detailTarget.supplierOrderNumber} />
                        <DetailField label="N° BL Cosium" value={detailTarget.cosiumDeliveryNoteNumber} />
                        <DetailField
                          label="Clôture automatique"
                          value={detailTarget.automaticallyClosedAt ? formatDateTime(detailTarget.automaticallyClosedAt) : ''}
                        />
                        <DetailField label="Marque" value={detailTarget.brand} />
                        <DetailField label="Modèle" value={detailTarget.model} />
                        <DetailField label="N° de série" value={detailTarget.serialNumber} />
                        <DetailField label="Taille / coloris" value={[detailTarget.size, detailTarget.color].filter(Boolean).join(' / ')} />
                      </Box>
                      {detailTarget.comment && (
                        <Box sx={{ mt: 2 }}>
                          <DetailField label="Commentaire initial" value={detailTarget.comment} />
                        </Box>
                      )}
                    </DetailCardSection>
                  </Box>
                  {/* Suivi rapide depuis l'œil : historique et journal en lecture seule. */}
                  <SavStatusWorkflowVertical row={detailTarget} />
                  <SavActivityHistory row={detailTarget} />
                </>
                )}

                {detailMode === 'history' && (
                <>
                <DetailCardSection title="Références commande" icon={<AssignmentRoundedIcon />}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                    <DetailField label="N° commande fournisseur" value={detailTarget.supplierOrderNumber} />
                    <DetailField label="N° BL Cosium" value={detailTarget.cosiumDeliveryNoteNumber} />
                    <DetailField label="Modèle" value={detailTarget.model} />
                    <DetailField label="Taille" value={detailTarget.size} />
                    <DetailField label="Coloris" value={detailTarget.color} />
                    <DetailField label="Pièce à remplacer" value={detailTarget.replacementPart} />
                    <DetailField label="Garantie" value={detailTarget.warrantyLabel || detailTarget.warrantyStatus} />
                    <DetailField label="Client" value={detailTarget.customerName} />
                    <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                      <DetailField label="Commentaire" value={detailTarget.comment} />
                    </Box>
                  </Box>
                </DetailCardSection>
                <SavStatusWorkflowVertical row={detailTarget}>
                  {canUpdateStatus(detailTarget) && (
                    <StatusReplyForm
                      detailTarget={detailTarget}
                      statusForm={statusForm}
                      setStatusForm={setStatusForm}
                      statusError={statusError}
                      statusSaving={statusSaving}
                      statusAttachmentFiles={statusAttachmentFiles}
                      canStoreAnswerInfo={canStoreAnswerInfo}
                      canStoreClose={canStoreClose}
                      handleStatusSubmit={handleStatusSubmit}
                      handleStatusAttachmentChange={handleStatusAttachmentChange}
                      removeSelectedStatusAttachment={removeSelectedStatusAttachment}
                    />
                  )}
                </SavStatusWorkflowVertical>
                <SavActivityHistory row={detailTarget} />
                </>
                )}
              </Stack>

            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Button onClick={() => { setDetailTarget(null); setDetailMode('details'); setStatusTarget(null); setStatusError(''); setStatusAttachmentFiles([]); }}>Fermer</Button>
          {detailTarget && (
            <>
              {canEditRequest(detailTarget) && (
                <Button startIcon={<EditIcon />} onClick={() => handleOpenEdit(detailTarget)}>
                  Modifier
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(supplierOrderTarget)}
        onClose={() => {
          if (supplierOrderSaving) return;
          setSupplierOrderTarget(null);
          setSupplierOrderError('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleSupplierOrderSubmit}>
          <DialogTitle>Saisir le numéro de commande fournisseur</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {supplierOrderError && <Alert severity="error">{supplierOrderError}</Alert>}
              {supplierOrderTarget && (
                <Alert severity="info">
                  {supplierOrderTarget.reference || getSavReference(supplierOrderTarget)} · {supplierOrderTarget.supplier || 'Fournisseur non renseigné'}
                </Alert>
              )}
              <TextField
                label="Numéro de commande fournisseur"
                value={supplierOrderForm.supplierOrderNumber}
                onChange={(event) => setSupplierOrderForm((current) => ({ ...current, supplierOrderNumber: event.target.value }))}
                required
                autoFocus
                fullWidth
              />
              <TextField
                label="Commentaire"
                value={supplierOrderForm.comment}
                onChange={(event) => setSupplierOrderForm((current) => ({ ...current, comment: event.target.value }))}
                multiline
                minRows={3}
                fullWidth
                placeholder="Confirmation reçue du fournisseur"
              />
              <Alert severity="success">
                L’enregistrement du numéro fera passer le dossier en statut <strong>Commandé</strong>.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSupplierOrderTarget(null)} disabled={supplierOrderSaving}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={supplierOrderSaving}>
              {supplierOrderSaving ? 'Enregistrement…' : 'Enregistrer le numéro'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog
        open={bulkStatusOpen}
        onClose={() => {
          if (bulkStatusSaving) return;
          setBulkStatusOpen(false);
          setBulkStatusError('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleBulkStatusSubmit}>
          <DialogTitle>Suivi multiple</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {bulkStatusError && <Alert severity="error">{bulkStatusError}</Alert>}
              <Alert severity="info">
                Le même statut et le même commentaire seront ajoutés à l’historique des {selectedSavRows.length} dossiers sélectionnés.
              </Alert>
              <TextField
                label="Statut"
                select
                value={bulkStatusForm.status}
                onChange={(event) => setBulkStatusForm((current) => ({ ...current, status: event.target.value }))}
                required
                fullWidth
              >
                {renderStatusOptionGroups()}
              </TextField>
              {bulkStatusForm.status === 'returned_store' && (
                <TextField
                  label="Numéro de BL Cosium"
                  value={bulkStatusForm.cosiumDeliveryNoteNumber || ''}
                  onChange={(event) => setBulkStatusForm((current) => ({ ...current, cosiumDeliveryNoteNumber: event.target.value }))}
                  required
                  fullWidth
                  helperText="Ce même numéro sera enregistré sur les dossiers sélectionnés."
                />
              )}
              {bulkStatusForm.status === 'ordered' && (
                <TextField
                  label="Numéro de commande fournisseur"
                  value={bulkStatusForm.supplierOrderNumber || ''}
                  onChange={(event) => setBulkStatusForm((current) => ({ ...current, supplierOrderNumber: event.target.value }))}
                  required
                  fullWidth
                  helperText="Ce même numéro sera enregistré sur les dossiers sélectionnés."
                />
              )}
              <TextField
                label="Commentaire du changement"
                value={bulkStatusForm.comment}
                onChange={(event) => setBulkStatusForm((current) => ({ ...current, comment: event.target.value }))}
                required={bulkStatusForm.status !== 'ordered'}
                multiline
                minRows={4}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setBulkStatusOpen(false); setBulkStatusError(''); }} disabled={bulkStatusSaving}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={bulkStatusSaving || !selectedSavRows.length}>
              {bulkStatusSaving ? 'Mise à jour…' : `Mettre à jour ${selectedSavRows.length} dossier(s)`}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog
        open={Boolean(statusTarget) && !detailTarget}
        onClose={() => {
          if (statusSaving) return;
          setStatusError('');
          setStatusTarget(null);
          setStatusAttachmentFiles([]);
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleStatusSubmit}>
          <DialogTitle>
            {statusTarget && canStoreAnswerInfo(statusTarget)
                ? statusTarget.status === 'waiting_store_info'
                  ? 'Répondre à la Logistique'
                  : 'Nouveau commentaire pour la Logistique'
                : statusTarget && canStoreClose(statusTarget)
                  ? 'Clôturer le dossier'
                  : 'Mettre à jour le suivi Logistique'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {statusError && <Alert severity="error">{statusError}</Alert>}
              {statusTarget && (
                <Alert severity="info">
                  {statusTarget.reference || getSavReference(statusTarget)} · {statusTarget.customerName || 'Client non renseigné'}
                </Alert>
              )}
              <TextField
                label="Statut Logistique"
                select
                value={statusForm.status}
                onChange={(event) => setStatusForm((current) => ({ ...current, status: event.target.value }))}
                disabled={Boolean(statusTarget && (canStoreAnswerInfo(statusTarget) || canStoreClose(statusTarget)))}
                required
                fullWidth
              >
                {statusForm.status === 'new_comment' && <MenuItem value="new_comment">Statut inchangé</MenuItem>}
                {renderStatusOptionGroups()}
              </TextField>
              {statusForm.status === 'returned_store' && (
                <TextField
                  label="Numéro de BL Cosium"
                  value={statusForm.cosiumDeliveryNoteNumber || ''}
                  onChange={(event) => setStatusForm((current) => ({ ...current, cosiumDeliveryNoteNumber: event.target.value }))}
                  required
                  fullWidth
                  helperText="Obligatoire pour envoyer le dossier au magasin."
                />
              )}
              {statusForm.status === 'ordered' && (
                <TextField
                  label="Numéro de commande fournisseur"
                  value={statusForm.supplierOrderNumber || ''}
                  onChange={(event) => setStatusForm((current) => ({ ...current, supplierOrderNumber: event.target.value }))}
                  required
                  fullWidth
                  helperText="Obligatoire pour passer le dossier à Commandé."
                />
              )}
              <TextField
                label="Commentaire du changement"
                value={statusForm.comment}
                onChange={(event) => setStatusForm((current) => ({ ...current, comment: event.target.value }))}
                required={statusForm.status !== 'ordered'}
                multiline
                minRows={3}
                fullWidth
              />
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} sx={{ alignSelf: { sm: 'flex-start' } }}>
                    Ajouter des fichiers
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                      onChange={handleStatusAttachmentChange}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Photos, PDF ou documents pour compléter les informations manquantes.
                  </Typography>
                </Stack>
                {statusAttachmentFiles.length > 0 && (
                  <Stack spacing={0.75}>
                    {statusAttachmentFiles.map((file, index) => (
                      <Stack
                        key={`${file.name}-${index}`}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.75 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                          {file.name} · {formatFileSize(file.size)}
                        </Typography>
                        <IconButton size="small" onClick={() => removeSelectedStatusAttachment(index)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setStatusError(''); setStatusTarget(null); setStatusAttachmentFiles([]); }} disabled={statusSaving}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={statusSaving}>
              {statusSaving
                ? 'Mise à jour…'
                : statusTarget && canStoreAnswerInfo(statusTarget)
                    ? statusForm.status === 'store_answer' ? 'Envoyer la réponse' : 'Ajouter le commentaire'
                    : 'Mettre à jour'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(documentTarget)} onClose={() => setDocumentTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 950 }} noWrap>
                Pièces jointes
              </Typography>
              {documentTarget && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {getShortSavReference(documentTarget)} · {documentTarget.customerName || 'Client non renseigné'}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setDocumentTarget(null)} aria-label="Fermer">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {documentTarget?.attachments?.length ? (
            <Stack spacing={1}>
              {documentTarget.attachments.map((attachment) => (
                <Stack
                  key={attachment._id}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 1.25, py: 1 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <AttachFileIcon fontSize="small" color="primary" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={attachment.originalName || attachment.fileName} sx={{ fontWeight: 850 }}>
                        {attachment.originalName || attachment.fileName || 'Document'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(attachment.size)} · {formatDateTime(attachment.uploadedAt)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Voir dans l'application">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const row = documentTarget;
                          setDocumentTarget(null);
                          handleOpenAttachment(row, attachment);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Télécharger">
                      <IconButton size="small" onClick={() => handleDownloadAttachment(documentTarget, attachment)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">Aucune pièce jointe sur ce dossier.</Alert>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={preview.open} onClose={handleClosePreview} fullWidth maxWidth="lg">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap>
              {preview.title}
            </Typography>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ height: { xs: '70vh', md: '78vh' }, p: 0 }}>
          {preview.mimeType.startsWith('image/') ? (
            <Box
              component="img"
              src={preview.url}
              alt={preview.title}
              sx={{ width: '100%', height: '100%', objectFit: 'contain', bgcolor: 'background.default' }}
            />
          ) : preview.mimeType === 'application/pdf' ? (
            <Box
              component="iframe"
              src={preview.url}
              title={preview.title}
              sx={{ width: '100%', height: '100%', border: 0, bgcolor: 'background.default' }}
            />
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: '100%', p: 3 }}>
              <Alert severity="info">Ce type de document ne peut pas être prévisualisé directement par le navigateur.</Alert>
              <Button component="a" href={preview.url} download={preview.title} variant="contained" startIcon={<DownloadIcon />}>
                Télécharger
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        itemLabel={`le dossier Logistique ${getSavReference(deleteTarget)}`}
        description={`Confirmer la suppression du dossier Logistique ${getSavReference(deleteTarget)} ?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget._id)}
      />

      <LogisticsPortalDialog
        open={needsLogisticsCollaborator}
        onSubmit={handleLogisticsPortalSubmit}
        onLogout={logout}
        loading={portalLoading}
        error={portalError}
      />
    </Stack>
  );
}
